import { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from "react";

import useLeagueSettings from './hooks/useLeagueSettings';
import { fonts, theme } from './data/theme';
import { PLAYERS, UNIQUE_PLAYERS, POS_COLORS } from './data/players';
import { ARCHETYPES, SPEED_SETTINGS, MOCK_ROSTER_SIZE } from './data/archetypes';
import { BYE_WEEKS_2026 } from './data/scheduleData';
import usePricing from './hooks/usePricing';
import useBuilderState from './hooks/useBuilderState';
import usePlanState from './hooks/usePlanState';
import useDraftBoard from './hooks/useDraftBoard';
import useMockDraft from './hooks/useMockDraft';

import { projectPoints, projectGames } from './utils/projectionSource';
import { buildRosterSlots, buildDraftTeams, simulateDraftSync } from './engine/simulation';
import { buildSmartTeam } from './engine/teamBuilder';
import SoundEngine from './engine/sounds';

import Header from './components/Header';
import UpdateBanner from './components/UpdateBanner';
import TeamGrid from './components/TeamGrid';
import PlayerList from './components/PlayerList';
import MockSetup from './components/MockSetup';
import AuctionCard from './components/AuctionCard';
import AvoidWarning from './components/AvoidWarning';
import ResultsScreen from './components/ResultsScreen';
import SettingsModal from './components/SettingsModal';
import ViewAllRostersModal from './components/ViewAllRostersModal';
import LandingPage from './components/LandingPage';
import AboutPage from './components/AboutPage';
import TierBuilder from './components/TierBuilder';
import ProjectionLab from './components/ProjectionLab';
import DraftPlan from './components/DraftPlan/DraftPlan';
import ErrorBoundary from './components/ErrorBoundary';
import OnboardingSheet from './components/OnboardingSheet';
import { LeagueContextLine } from './components/LeagueChip';
import TierViewer from './components/TierViewer';
import GuidedTutorial, { HINT_CONFIG } from './components/GuidedTutorial';
import RecapLanding from './components/Recap/RecapLanding';
import RecapImport, { RECAP_PLATFORMS } from './components/Recap/RecapImport';
import EmailGate from './components/Recap/EmailGate';
import RecapReport from './components/Recap/RecapReport';
import { pipelineStateFromExport } from './utils/almanacJsonImport';
import { captureAlmanacShareViewed, captureAlmanacShareViewFailed } from './utils/recapAnalytics';
import DraftRecapEmpty from './components/DraftRecapEmpty';
import LiveBoard from './components/LiveBoard/LiveBoard';
import useLiveBoard from './hooks/useLiveBoard';
import { liveBoardProgress } from './utils/liveBoard';
import { resolveLiveBoardFlag } from './utils/liveBoardFlag';
import { resolveRateFlag } from './utils/rateFlag';
import { usePlayerCard } from './context/PlayerCardContext';
import { captureMockDraftStarted, captureMockDraftCompleted } from './utils/mockAnalytics';
import { leagueFormatLabel } from './utils/leagueFormat';
import { marketAnchorPresetFor } from './utils/marketAnchor';
import { captureFeatureView } from './utils/projlabAnalytics';

import './styles/common.css';
import './styles/builder.css';
import './styles/auction.css';
import './styles/recap.css';


function RosterAvatar({ player, size = 22 }) {
  const [err, setErr] = useState(false);
  const sid = player?.sleeper_id;
  const pos = player?.pos || "BN";
  const bg = POS_COLORS[pos]?.bg || "#64748b";
  if (sid && pos !== "DEF" && pos !== "K" && !err) {
    return <img src={`https://sleepercdn.com/content/nfl/players/${sid}.jpg`} alt="" onError={() => setErr(true)} style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0, background: "#0d1320" }} />;
  }
  const parts = (player?.name || "").split(" ");
  const label = pos === "DEF" || pos === "K" ? (player?.team || pos) : (parts.length >= 2 ? parts[0][0] + parts[parts.length - 1][0] : "?").toUpperCase();
  return <div style={{ width: size, height: size, borderRadius: "50%", flexShrink: 0, background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: pos === "DEF" || pos === "K" ? 8 : 9, fontWeight: 700, color: "#fff", lineHeight: 1 }}>{label}</div>;
}

// Held while /almanac?share=<id> is in flight. Deliberately plain: the payload
// is ~214KB stored and this is usually gone in well under a second, so anything
// more elaborate would flash.
function SharedAlmanacLoading() {
  return (
    <div style={{
      minHeight: "60vh",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      gap: 10, padding: 40, textAlign: "center",
      fontFamily: theme.fonts.display,
    }}>
      <div style={{
        fontSize: 11, letterSpacing: 3, color: theme.colors.brand.accent,
        textTransform: "uppercase", fontWeight: theme.fontWeight.heavy,
      }}>League Almanac</div>
      <div style={{
        fontSize: 18, color: theme.colors.text.secondary, fontFamily: theme.fonts.body,
      }}>Loading shared almanac…</div>
    </div>
  );
}

// 🔴 THIS BANNER IS THE ENTIRE GROWTH PAYOFF OF THE SHARE ARC. Every shared
// almanac is seen by a whole league of people who do not use this app, looking
// at data they care about. One line, brand register, no dismissal mechanics —
// visible without competing with the report it sits above. Plain <a>, so it is
// a real navigation to /almanac with no ?share, which lands on the landing.
function SharedAlmanacBanner() {
  return (
    <div style={{
      background: theme.helpers.brandBg(),
      borderBottom: `1px solid ${theme.colors.brand.accent}40`,
      padding: "10px 16px",
      textAlign: "center",
      fontFamily: theme.fonts.body,
      fontSize: 13,
      color: theme.colors.text.secondary,
    }}>
      Shared from the Mock1 League Almanac —{" "}
      <a href="/almanac" style={{
        color: theme.colors.brand.accent,
        fontWeight: theme.fontWeight.heavy,
        textDecoration: "none",
      }}>preserve your own league's history, free →</a>
    </div>
  );
}

// OnboardingSheet's localStorage flag for the Team Builder intro. A constant
// because it now has TWO readers — the sheet that consumes it, and
// openSettingsFromAlmanac, which sets it to suppress the intro. As a literal in
// both places, renaming one would silently stop the suppression working while
// everything still compiled.
const ONBOARDING_BUILDER_KEY = "onboarding-builder";

export default function AuctionDraftBuilder() {
  // ── League settings (settings, savedLeagues, tiers) ──────────────────────
  const {
    settings, setSettings,
    showSettings, setShowSettings,
    savedLeagues, setSavedLeagues,
    updateActiveLeague,
    activeLeagueName, setActiveLeagueName,
    loadTierOverridesForLeague,
    userTierOverrides, setUserTierOverrides,
    PLATFORM_DEFAULTS,
    importNotice, setImportNotice,
    applySettings: _applySettings,
    loadLeagueFromList: _loadLeagueFromList,
    setPositionDials,
    setMarketAnchor,
    loadDefaultPlatform: _loadDefaultPlatform,
  } = useLeagueSettings();

  // ── Sync scoring to PlayerCard context ────────────────────────────────────
  const { setScoring: _setPlayerCardScoring, setDynamicPrices: _setPlayerCardPrices, setBudget: _setPlayerCardBudget, setActiveLeagueName: _setPlayerCardLeague, setStars: _setPlayerCardStars, setStarToggle: _setPlayerCardStarToggle } = usePlayerCard();
  useEffect(() => { _setPlayerCardScoring(settings.scoring); }, [settings.scoring, _setPlayerCardScoring]);
  useEffect(() => { _setPlayerCardBudget(settings.budget); }, [settings.budget, _setPlayerCardBudget]);
  useEffect(() => { _setPlayerCardLeague(activeLeagueName); }, [activeLeagueName, _setPlayerCardLeague]);

  // ── Pricing, projections & VORP ───────────────────────────────────────────
  // The saved league record behind activeLeagueName. usePricing only reads it
  // for the opt-in calibrate flag and auctionHistory; undefined (no active
  // league, or a name with no saved record) is the uncalibrated path.
  const activeLeague = useMemo(
    () => savedLeagues.find(l => l.name === activeLeagueName),
    [savedLeagues, activeLeagueName]
  );
  const {
    customStats, setCustomStats,
    expandedPlayer, setExpandedPlayer,
    getPlayerStats, getPlayerProj, getPlayerGames,
    replacementLevels, getPlayerVorp,
    dynamicPrices,
    dialsOverrideLeague,
    marketAnchor,
    resetPlayerStats,
  } = usePricing(settings, userTierOverrides, activeLeague);

  // Share the league-dynamic price map with the shared card portal so PlayerCard
  // shows the same $ as Team Builder. (Declared after usePricing so dynamicPrices
  // is in scope.)
  useEffect(() => { _setPlayerCardPrices(dynamicPrices); }, [dynamicPrices, _setPlayerCardPrices]);

  // Same registration for "my guys", declared after usePlanState below so
  // planApi is in scope — see the effects just under it.

  // ── Builder state (roster, filters, builds, computed) ─────────────────────
  // Builder price = the league-dynamic price (supersedes the legacy player.value;
  // Jul 23 decision). Same number the Mock player list and PlayerCard show.
  const builderGetValue = useCallback((player) => {
    // MUST use the composite name-pos key — a miss silently shows $1.
    return dynamicPrices.get(`${player.name}-${player.pos}`) ?? 1;
  }, [dynamicPrices]);
  const pricing = {
    getPlayerValue: builderGetValue, getPlayerProj, getPlayerGames, getPlayerVorp,
    dynamicPrices, replacementLevels,
    getPlayerStats,
  };
  const rosterSlots = useMemo(() => buildRosterSlots(settings.positions), [settings.positions]);
  const {
    roster, setRoster,
    lockedSlots, setLockedSlots,
    search, setSearch,
    posFilter, setPosFilter,
    sortBy, setSortBy,
    mobileTab, setMobileTab,
    showBuilderMenu, setShowBuilderMenu,
    assigningPlayer, setAssigningPlayer,
    targetSlotId, setTargetSlotId,
    showBuildPanel, setShowBuildPanel,
    selectedArchetype, setSelectedArchetype,
    buildModifiers, setBuildModifiers,
    buildStep, setBuildStep,
    buildName, setBuildName,
    showSaved, setShowSaved,
    showSaveBuildDialog, setShowSaveBuildDialog,
    savedBuilds, setSavedBuilds,
    FLEX_POSITIONS, SFLEX_POSITIONS, BUDGET,
    rosterHasPlayers,
    rosteredPlayerNames,
    spent, emptySlots, maxBid, remaining,
    projectedTotal, starterProjectedTotal, week1Projection,
    spendingByPos, filteredPlayers,
    getAvailableSlots,
    togglePosFilter, isFilterActive,
    addPlayer, handlePlayerClick,
    removePlayer, toggleLock,
    saveBuild, loadBuild, deleteBuild,
    clearRoster,
  } = useBuilderState(settings, pricing, rosterSlots, activeLeagueName);

  // Draft Plan owns its own state and its own localStorage; it reads the SAME
  // dynamicPrices the board and builderGetValue read, so the plan cannot drift
  // from the board. Placed here because rosterSlots and dynamicPrices are both
  // already in scope.
  const planApi = usePlanState(settings, rosterSlots, dynamicPrices, activeLeagueName);

  // 🔴 The star sheet reaches the shared card portal the same way dynamicPrices
  // does, and for the same reason: PlayerCardProvider sits ABOVE this tree, so
  // it cannot call usePlanState — and usePlanState must not move, because the
  // sheet belongs with the plans and the localStorage that persists them.
  //
  // 🔴 This works from EVERY tab because usePlanState is called unconditionally
  // here, beside useBuilderState, and not gated on appMode. So starring a
  // player from his card in Team Builder writes the same league sheet Draft
  // Plan reads, with no mounting order to get right. Do not move this call
  // inside an appMode branch.
  useEffect(() => { _setPlayerCardStars(planApi.leagueStars); }, [planApi.leagueStars, _setPlayerCardStars]);
  useEffect(() => { _setPlayerCardStarToggle(planApi.toggleStar); }, [planApi.toggleStar, _setPlayerCardStarToggle]);

  // An auction in progress outranks the landing page. A refresh at pick 47 — a
  // laptop sleeping, a stray Cmd+R with eleven people watching — must come back
  // to the board, not to the marketing page with no sign the picks still exist.
  // Read once at mount; Start Over clears the board and with it this branch.
  // The Live Board is a PUBLIC BETA as of Aug 26 — liveBoardFlag.js is
  // default-on, `?live=0` is the opt-out and persists, `?live=1` still works
  // and is a no-op on a stock profile. Read once, at start.
  const [liveEnabled] = useState(() => {
    try { return resolveLiveBoardFlag(); } catch { return true; }
  });
  // /rate launched Sep 4 2026: default ON, so the landing card is offered to
  // everyone and `?rate=0` is the browser-level opt-out. Same shape as the line
  // above and for the same reason — resolveRateFlag applies ?rate= and persists
  // it, so it runs once. The catch mirrors liveEnabled's: a launched feature
  // fails visible.
  const [rateEnabled] = useState(() => {
    try { return resolveRateFlag(); } catch { return true; }
  });

  // 🔴 GATE THE MODE, NOT JUST THE BUTTON. This restore can set appMode to
  // "live" on its own, so with the flag off — now only by explicit `?live=0` —
  // a user carrying a saved board in localStorage would land on a surface whose
  // nav tab does not exist, with no way back except clearing storage.
  const [liveInProgress] = useState(() => {
    try { return liveEnabled && liveBoardProgress(activeLeagueName) > 0; } catch { return false; }
  });

  const [showLanding, setShowLanding] = useState(() => !liveInProgress);
  const [scrollToAffordable, setScrollToAffordable] = useState(0);

  // Pick the touch-verb by input type, not viewport (pointer type is stable
  // mid-session, so evaluate once per render — no listener needed).
  const isTouch = window.matchMedia?.("(pointer: coarse)")?.matches;

  // Mode toggle
  const [appMode, setAppMode] = useState(() => (liveInProgress ? "live" : "builder"));

  // Recap stage — separate from appMode so the recap flow can have its own
  // internal navigation (landing → importing → ready) without leaking into
  // the main builder/tiers/mock/projections switcher.
  const [recapStage, setRecapStage] = useState("landing"); // 'landing' | 'loading-shared' | 'importing-sleeper' | 'importing-yahoo' | 'ready'
  const [recapPipelineState, setRecapPipelineState] = useState(null);
  // Shared-view state. `isSharedView` is what routes AROUND EmailGate and turns
  // the growth banner on; `shareError` is the message a broken link leaves on
  // the landing. Both are set only by the effect below.
  const [isSharedView, setIsSharedView] = useState(false);
  const [shareError, setShareError] = useState(null);
  // Read ONCE at mount, like App.js's /watch ?b= read. A later re-read could
  // see a URL the user has since navigated away from.
  const [shareId] = useState(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("share");
  });

  // /almanac?share=<id> — fetch a published almanac and render it with no
  // steps at all. The whole promise of the link is that a leaguemate who has
  // never heard of this app clicks it and sees the report.
  //
  // 🔴 THE ID SHAPE IS CHECKED BEFORE THE FETCH. It is the same regex the
  // server validates with (api/almanac-share.js), so a truncated link — the
  // common failure, since these get pasted into group texts — costs a round
  // trip and gets the same message either way.
  useEffect(() => {
    if (!shareId) return;
    let cancelled = false;
    const fail = (reason) => {
      if (cancelled) return;
      captureAlmanacShareViewFailed({ reason });
      setShareError("That share link didn't load — it may have been mistyped or removed.");
      setRecapStage("landing");
    };
    if (!/^[A-Za-z0-9_-]{8,64}$/.test(shareId)) {
      fail("malformed share id");
      return;
    }
    setRecapStage("loading-shared");
    (async () => {
      try {
        const res = await fetch(`/api/almanac-share?id=${encodeURIComponent(shareId)}`);
        let body = null;
        try { body = await res.json(); } catch { /* falls through to the status */ }
        if (!res.ok || !body?.ok) throw new Error(body?.error || `read failed (${res.status})`);
        // pipelineStateFromExport throws AlmanacImportError on a corrupt
        // export — caught here with everything else, since the viewer can do
        // nothing about any of them and the message is the same.
        const state = pipelineStateFromExport(body.export);
        if (cancelled) return;
        setRecapPipelineState(state);
        setIsSharedView(true);
        setRecapStage("ready");
        document.title = `${state?.leagueMetadata?.name || "League"} — League Almanac`;
        captureAlmanacShareViewed({
          platform: state?.platform,
          seasonCount: state?.seasons?.length || 0,
        });
      } catch (err) {
        fail(err?.message || "shared almanac could not be loaded");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL routing (lightweight — no react-router; pushState on entry, popstate to
  // leave). /almanac → the League Almanac (appMode "recap"). /recap → the draft
  // recap surface (appMode "draftrecap"); these are DIFFERENT experiences —
  // /recap must not fall into the Almanac. /live → the Live Board, and only
  // with the flag on — see the note inside.
  useEffect(() => {
    /**
     * 🔴 /live IS GATED BY THE FLAG, AND THAT GATE IS THE WHOLE RISK OF ADDING
     * IT. liveBoardFlag.js guards exactly two things — the nav button and the
     * resume path — so a third entry point that rendered without the flag would
     * undo the gating this all exists for. With the flag off, `routeFor`
     * returns null and /live is an unrouted path: the landing page, exactly as
     * before. `?live=1` still works in the same request, because
     * resolveLiveBoardFlag runs in a useState initialiser above this effect.
     *
     * WHY IT NEEDED TO EXIST. Closing the operator tab on draft night left no
     * address that got you back: the only two ways in were the nav button and
     * the auto-resume, and the auto-resume counts SALES
     * (`liveBoardProgress > 0`). A board with teams named and a player on the
     * block but nothing sold yet returns 0 and does not resume — which is
     * precisely the setup window, with eleven people already sitting down.
     *
     * ⚠️ THE RESUME HEURISTIC IS DELIBERATELY LEFT ALONE. Relaxing it to "a
     * board exists" would drop Ben into live mode every time he opened the app
     * between drafts until he hit Start Over — one annoyance traded for a
     * worse one. The route covers the same gap without that.
     *
     * ⚠️ `liveEnabled` is captured from the first render, which is correct
     * here: it comes from a useState initialiser and never changes for the
     * lifetime of this component.
     */
    const routeFor = (p) => p === "/almanac" ? "recap"
      : p === "/recap" ? "draftrecap"
        : p === "/about" ? "about"
          : (p === "/live" && liveEnabled) ? "live"
            : null;
    const titleFor = (mode) => mode === "recap" ? "Mock 1 — League Almanac"
      : mode === "about" ? "Mock 1 — About"
        : mode === "live" ? "Mock 1 — Live Board"
          : "Mock 1 — Draft Recap";
    const applyRoute = (mode) => {
      setShowLanding(false);
      setAppMode(mode);
      document.title = titleFor(mode);
    };
    const initial = routeFor(window.location.pathname);
    if (initial) applyRoute(initial);
    // Derive the target PURELY from the URL — never from captured state, which
    // would be stale under the [] deps below. routeFor(pathname) === null means
    // we've navigated back to a non-routed path (e.g. "/"), so go home. A
    // pushState never fires popstate, so home-on-null can't clobber a real view.
    const onPop = () => {
      const mode = routeFor(window.location.pathname);
      if (mode) {
        applyRoute(mode);
      } else {
        setShowLanding(true);
        setAppMode("builder");
        document.title = "Mock 1 — Fantasy Auction Draft Simulator";
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ⚠️ DO NOT RE-ARM THIS. It is the mid-draft live-recompute hop.
  //
  // This re-reads the PRICE tier key (userTierSheet_<league>) into state on
  // every appMode transition away from "tiers" — including one taken in the
  // middle of a live auction. It is inert today only because nothing writes
  // that key any anymore: the Tier Builder writes userDisplayTierMoves_<league>,
  // and index.js:11 purges userTierSheet* before render on every page load.
  // So loadTierOverridesForLeague always returns {}.
  //
  // If the price key is ever written again, THIS is the line that moves
  // dynamicPrices under a half-finished draft with no reload and no warning —
  // and the damage is not subtle. On a contaminated board the diagnostic
  // measured a 74× sticker/sale seam: Ja'Marr Chase rendering "$6" and selling
  // for $74, because must-fill and hardCap deploy the room's full $200/team
  // regardless of what the board says. Prices cannot hard-break a draft; they
  // can only make the displayed number a lie, at that magnitude.
  //
  // Before touching this, read docs/reference/tier-diagnostic-aug9.md §3(b) and
  // §2, and run the gate that keeps the severance severed:
  //   CI=true npx react-scripts test --testPathPattern=severanceGate --watchAll=false
  // The gate pins the clean seeded digest at 1358333851 and proves it moves to
  // 1697339754 on a contaminated board, so re-arming this without noticing is
  // exactly what it exists to catch.
  //
  // Display tiering has its own, separate live path and is unaffected:
  // TierViewer reads loadOverrides(activeLeagueName) and regroups mid-draft.
  useEffect(() => {
    if (appMode !== "tiers") {
      setUserTierOverrides(loadTierOverridesForLeague(activeLeagueName));
    }
  }, [appMode, activeLeagueName, loadTierOverridesForLeague, setUserTierOverrides]);

  // Analytics: one feature_view per top-level feature activation. Gated on
  // !showLanding (the landing page isn't a feature) and keyed on both so
  // re-launching the mode you're already in from the landing still counts.
  useEffect(() => {
    if (showLanding) return;
    // ⚠️ "plan" IS DRAFT PLAN AND "builder" IS THE OLD TEAM BUILDER. They are
    // two different sections that ship beside each other, and `plan` was
    // missing from this map from the aug27 beta until aug28 — which is the
    // whole reason Draft Plan had no usage number at all. Do not merge them.
    const FEATURE_BY_MODE = { builder: "team_builder", plan: "draft_plan", tiers: "tiers", mock: "mock_draft", projections: "projection_lab", recap: "almanac", live: "live_board" };
    const feature = FEATURE_BY_MODE[appMode];
    if (feature) captureFeatureView({ feature });
  }, [appMode, showLanding]);

  // ── Draft Board state + computed ──────────────────────────────────────────
  const {
    draftTeams, setDraftTeams,
    draftPicks, setDraftPicks,
    draftingPlayer, setDraftingPlayer,
    draftPrice, setDraftPrice,
    draftTeamId, setDraftTeamId,
    draftNominatorId, setDraftNominatorId,
    showDraftRecap, setShowDraftRecap,
    draftSearch, setDraftSearch,
    draftPosFilter, setDraftPosFilter,
    editingTeamName, setEditingTeamName,
    draftMobileTab, setDraftMobileTab,
    myTargets, setMyTargets,
    assistantTab, setAssistantTab,
    draftedPlayerNames,
    teamRosters, teamBudgets,
    draftFilteredPlayers,
    teamMaxBids, marketTrends,
    teamNeeds, nominationSuggestions,
    positionalScarcity, targetStatus,
    startDraft, confirmDraft,
    undoLastPick, renameTeam,
    resetDraft, toggleTarget,
  } = useDraftBoard(settings, pricing, rosterSlots, activeLeagueName);

  // ── Tutorial hints (must be before useMockDraft so tutorialActive param works) ──
  const [tutorialHint, setTutorialHint] = useState(() => {
    try { if (localStorage.getItem("mock1-tutorial-complete") === "true") return null; } catch {}
    return "opponents";
  });
  const [placeBidReady, setPlaceBidReady] = useState(false);
  const [mobPanelTab, setMobPanelTab] = useState("players");
  // One definition, used both as the useMockDraft param and by the Resume
  // control below. It was previously inlined at the call site only; a second
  // copy of this expression would drift and the Resume control would disagree
  // with the gate it exists to clear.
  const tutorialActive = tutorialHint !== null && !(tutorialHint === "place-bid" && !placeBidReady);

  // ── Mock draft (state, effects, callbacks, auction loop) ──────────────────
  const {
    mockPhase, setMockPhase,
    mockUserOwner, setMockUserOwner,
    mockTeamConfigs, setMockTeamConfigs,
    effectiveTeamConfigs, modeledSeatCount,
    mockUserSlot, setMockUserSlot,
    mockSpeed, setMockSpeed,
    mockPickNumber, setMockPickNumber,
    mockNominatorIndex, setMockNominatorIndex,
    mockDraftedPlayers, setMockDraftedPlayers,
    mockDraftLog, setMockDraftLog,
    mockKeeperPicks,
    mockTeams, setMockTeams,
    mockCurrentPlayer, setMockCurrentPlayer,
    mockCurrentBid, setMockCurrentBid,
    mockCurrentBidder, setMockCurrentBidder,
    mockActiveBidders, setMockActiveBidders,
    mockSoldPlayer, setMockSoldPlayer,
    mockSearch, setMockSearch,
    mockPosFilter, setMockPosFilter,
    mockMobileTab, setMockMobileTab,
    mockShowRecap, setMockShowRecap,
    mockPaused, setMockPaused,
    mockAutopick, setMockAutopick,
    userSeatAiDriven, setUserSeatAiDriven,
    mockNomTimer, setMockNomTimer,
    mockNomTimerMode, setMockNomTimerMode, getNomSeconds,
    mockGoingPhase, setMockGoingPhase,
    mockNominatingPlayer, setMockNominatingPlayer,
    mockOpeningBid, setMockOpeningBid,
    mockExpandedTeam, setMockExpandedTeam,
    mockWatchlist, setMockWatchlist,
    myAvoids,
    mockWatchlistAlert, setMockWatchlistAlert,
    mockNominateOverlay, setMockNominateOverlay,
    mockNomGlow, setMockNomGlow,
    heartbeatPulse, setHeartbeatPulse,
    showMockOverflow, setShowMockOverflow,
    mockSpeedToast, setMockSpeedToast,
    mockMuted, setMockMuted,
    mockLeaveTarget, setMockLeaveTarget,
    mockExportText, setMockExportText,
    mockSavedDraft, setMockSavedDraft,
    mockEnforcerCaught, setMockEnforcerCaught,
    skipMode, setSkipMode,
    mockSpeedToastRef,
    heartbeatIntervalRef,
    mockBidTimerRef,
    mockUserPassedRef,
    mockNomTimerRef,
    mockSoldTimerRef,
    mockTickRef,
    mockTierAnchorsRef,
    mockInflationRef,
    skipModeRef,
    mockMutedRef,
    draftLogRef,
    mockDraftActive,
    mockDraftLive,
    mockAvailablePlayers,
    mockUserTeam,
    mockIsUserTurn,
    mockUserMaxBid,
    playMockSound,
    startMockDraft: _startMockDraft,
    resetMockDraft,
    resumeMockDraft,
    mockNominatePlayer,
    processSoldPlayer,
    mockPlaceBid,
    mockPassBid,
    toggleMockPaused,
    toggleWatchlist,
    toggleAvoid,
    autoFinishMock,
    startSkip,
    stopSkipMode,
    clearHandoffPause,
  } = useMockDraft(settings, appMode, setAppMode, dynamicPrices, userTierOverrides, roster, tutorialActive, activeLeagueName, activeLeague, showSettings);

  // Live Board state. Deliberately NOT part of useMockDraft and sharing nothing
  // with it — this surface runs during a real auction, so the mock draft's
  // freeze class of bug is structurally unable to reach it.
  const live = useLiveBoard(activeLeagueName, settings);

  // What TierViewer treats as gone: sold players AND kept players.
  //
  // TierViewer answers "which tiers still have someone in them" from its
  // draftPicks prop, so a keeper — which is in the drafted-set but never in the
  // draft log — would otherwise render as available at his board price, in the
  // one panel whose whole job is to show who is left. Concatenating the keeper
  // picks (already {player, price}, at the KEEPER price) fixes the sold-map, the
  // dimming, AND the top-available-tier auto-scroll without TierViewer needing
  // to know keepers exist. Identity-stable when there are no keepers, so the
  // non-keeper render path is untouched.
  const mockTierViewerPicks = useMemo(
    () => (mockKeeperPicks.length ? [...mockDraftLog, ...mockKeeperPicks] : mockDraftLog),
    [mockDraftLog, mockKeeperPicks]
  );

  // ── Skip-to-my-pick stop reason toast ────────────────────────────────────
  const [skipStopReason, setSkipStopReason] = useState(null);
  const skipStopTimerRef = useRef(null);

  // Detect when skip stops and determine the reason
  const prevSkipRef = useRef(false);
  useEffect(() => {
    if (prevSkipRef.current && !skipMode && mockPaused) {
      // Skip just stopped — determine why
      let reason;
      if (mockIsUserTurn) {
        reason = "Your turn to nominate";
      } else if (mockCurrentPlayer && mockWatchlist.has(mockCurrentPlayer.name)) {
        reason = "Watchlist player nominated!";
      } else if (mockCurrentPlayer) {
        // Must-fill position
        const userTeam = mockTeams[mockUserSlot];
        const posCounts = {};
        if (userTeam) userTeam.roster.forEach(r => { posCounts[r.player.pos] = (posCounts[r.player.pos] || 0) + 1; });
        const minReqs = { QB: 1, RB: 2, WR: 2, TE: 1, DEF: 1, K: 1 };
        const needed = (minReqs[mockCurrentPlayer.pos] || 0) - (posCounts[mockCurrentPlayer.pos] || 0);
        if (needed > 0) {
          reason = `Must-fill ${mockCurrentPlayer.pos} nominated!`;
        } else {
          reason = "Skip stopped";
        }
      } else {
        reason = "Your turn to nominate";
      }
      setSkipStopReason(reason);
      if (skipStopTimerRef.current) clearTimeout(skipStopTimerRef.current);
      skipStopTimerRef.current = setTimeout(() => setSkipStopReason(null), 3500);
    }
    prevSkipRef.current = skipMode;
  }, [skipMode, mockPaused, mockIsUserTurn, mockCurrentPlayer, mockWatchlist, mockTeams, mockUserSlot]);

  // ── View All Rosters modal ───────────────────────────────────────────────
  const [showAllRosters, setShowAllRosters] = useState(false);
  // Saved-builds panel: default to the active league only; toggle to see every build.
  const [showAllBuildLeagues, setShowAllBuildLeagues] = useState(false);
  // Stable so it can be handed to memoized children (TierBuilder,
  // LeagueContextLine) without defeating their memo on every parent render.
  // Declared up here with the other hooks — there are early returns below.
  const openSettings = useCallback(() => setShowSettings(true), [setShowSettings]);

  // "+ Create new league" opens the SAME modal, already on its create step —
  // one modal, one place leagues are made. The intent is per-open state, not
  // sticky: the modal is mounted conditionally, so it reads startInCreate once
  // on mount and closing clears it.
  const [settingsCreateIntent, setSettingsCreateIntent] = useState(false);
  const openCreateLeague = useCallback(() => {
    setSettingsCreateIntent(true);
    setShowSettings(true);
  }, [setShowSettings]);
  // The pencil opens the TABS, on Format. Creation is the only thing that gets
  // walked through a stepper; a returning editor came for one specific knob.
  const [settingsFormatIntent, setSettingsFormatIntent] = useState(false);
  const closeSettings = useCallback(() => {
    setShowSettings(false);
    setSettingsCreateIntent(false);
    setSettingsFormatIntent(false);
  }, [setShowSettings]);
  // "Done — take me to my league", the last step of the create stepper. Closing
  // alone would drop the user back on whichever screen they opened the modal
  // from — the header dropdown is reachable from every mode — and the league
  // they just built is a MOCK SETUP away from being useful. Guarded on a live
  // draft for form only: creating is already blocked while one is running.
  const finishLeagueCreate = useCallback(() => {
    closeSettings();
    if (!mockDraftLive) setAppMode("mock");
  }, [closeSettings, mockDraftLive, setAppMode]);
  // The modal idles the auction loop while it is open, and its button says
  // "Close & Resume" — so closing it resumes, unconditionally.
  //
  // It used to save/restore mockPaused through a ref, which stranded the draft:
  // the visibilitychange auto-pause (useMockDraft.js:999-1019, "PAUSE ONLY,
  // NEVER AUTO-RESUME") sets mockPaused BEFORE the modal opens, so the ref
  // captured true and "Close & Resume" restored true. loopIdle stayed set,
  // Effect 2 returned at :1503 writing no state, and the draft was dead with no
  // way back. Re-entry made it worse — :835 and :1783 have no guard, so a
  // second open overwrote the ref with the value the first open had just set.
  //
  // Resuming unconditionally is what the label promises. It can only override a
  // deliberate pause when the user has just clicked a button reading "Resume",
  // which is the outcome they asked for; the save/restore ref is gone rather
  // than merely corrected, so the stale-capture class cannot come back.
  const openAllRosters = useCallback(() => {
    setMockPaused(true);
    setShowAllRosters(true);
  }, [setMockPaused]);
  const closeAllRosters = useCallback(() => {
    setShowAllRosters(false);
    setMockPaused(false);
  }, [setMockPaused]);

  // R key toggle — only during active draft, and ignore when typing in inputs.
  // The appMode test keeps a parked draft from binding "r" on every other page
  // (third instance of the phase-only leak; found by the 02a8039 audit).
  useEffect(() => {
    const isActive = appMode === "mock" && (mockPhase === "nominating" || mockPhase === "bidding" || mockPhase === "sold");
    if (!isActive) return;
    const onKey = (e) => {
      if (e.key !== "r" && e.key !== "R") return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      if (showAllRosters) closeAllRosters(); else openAllRosters();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [appMode, mockPhase, showAllRosters, openAllRosters, closeAllRosters]);

  // Escape closes an expanded scoreboard roster — only during active draft,
  // and ignore when typing in inputs (same guard as the R-key handler).
  useEffect(() => {
    const isActive = mockPhase === "nominating" || mockPhase === "bidding" || mockPhase === "sold";
    if (!isActive || mockExpandedTeam === null) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      setMockExpandedTeam(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mockPhase, mockExpandedTeam, setMockExpandedTeam]);

  // Desktop scroll containment: lock the page while a draft is active so the
  // mouse wheel can't nudge the document. Safe because the wrapper is bound to
  // calc(100dvh - offset) — nothing sits below the fold — and inner panels keep
  // their own scrollbars.
  //
  // The class is added unconditionally — CSS is the ONLY width gate, since
  // `body.mock-draft-locked { overflow: hidden }` lives inside auction.css's
  // min-width:1101px block. That is deliberate and is why the narrow layout
  // below 1101px gets no desktop lock without any JS width check here. The
  // narrow layout has its own lock (`mob-auction-locked`, gated in JS in
  // useMockDraft) — see the four-site boundary note in auction.css.
  //
  // The appMode test is load-bearing: phase alone leaks the desktop scroll lock
  // onto every page when a live draft is parked (found Aug 2; second instance of
  // the missing-appMode disease after 96d05e6).
  useEffect(() => {
    const isActive = appMode === "mock" && (mockPhase === "nominating" || mockPhase === "bidding" || mockPhase === "sold");
    if (!isActive) return;
    document.body.classList.add("mock-draft-locked");
    return () => document.body.classList.remove("mock-draft-locked");
  }, [appMode, mockPhase]);

  // Desktop scroll containment: measure the active-draft wrapper's real distance
  // from the top of the document and expose it as --mock-top-offset so the CSS
  // can bound the layout with calc(100dvh - offset). Replaces a hardcoded guess
  // that under-counted the header + format line + controls row + margins.
  const mockDraftWrapperRef = useRef(null);
  useLayoutEffect(() => {
    const isActive = mockPhase === "nominating" || mockPhase === "bidding" || mockPhase === "sold";
    if (!isActive) return;
    let rafId = 0;
    let cancelled = false;
    const measure = () => {
      const el = mockDraftWrapperRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      // Subtract the wrapper's top position PLUS the mock container's 20px
      // bottom padding (which sits below the wrapper) so the wrapper clears the
      // fold instead of overflowing by that padding and re-triggering a scroll.
      let offset = Math.round(top) + 20;
      el.style.setProperty("--mock-top-offset", `${offset}px`);
      // Self-correcting passes: after the browser applies the height, fold any
      // residual page overflow into the offset. This used to ignore residuals of
      // 80px or more, which gave up in exactly the case that matters — a short
      // viewport under a tall header — and stranded content below a fold the
      // page is scroll-locked against (body.mock-draft-locked). Clamp the pass
      // COUNT, not the magnitude: oscillation stays bounded at two corrections,
      // but a large overflow is always absorbed.
      let pass = 0;
      cancelAnimationFrame(rafId);
      const correct = () => {
        if (cancelled) return;
        // Measure the WRAPPER's own bottom edge, not document.scrollHeight. The
        // document figure also counts overflow from inside the wrapper, which
        // shrinking the wrapper cannot resolve — chasing it eats usable height
        // and makes an unshrinkable child overflow further on every pass. Only
        // the wrapper's box extending past the fold is the offset's problem.
        const overflow = Math.round(el.getBoundingClientRect().bottom) - window.innerHeight;
        if (overflow <= 0 || pass >= 2) return;
        pass += 1;
        offset += overflow;
        el.style.setProperty("--mock-top-offset", `${offset}px`);
        rafId = requestAnimationFrame(correct);
      };
      rafId = requestAnimationFrame(correct);
    };
    measure();
    window.addEventListener("resize", measure);
    // The header stack reflows without firing a resize: .mock-controls-bar wraps
    // to a second row at some widths, and the league context line appears with a
    // saved workspace. Observe the stack directly so the offset tracks it. Safe
    // from feedback — the variable sizes only the wrapper BELOW these elements,
    // so re-measuring can never change what we observe.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) {
      const header = document.querySelector(".app-header");
      const controls = document.querySelector(".mock-controls-bar");
      if (header) ro.observe(header);
      if (controls) ro.observe(controls);
    }
    // Fonts swap in after first layout and grow the header with no resize event.
    // Warm-cache sessions usually miss this; cold-cache visitors hit it, which is
    // what made the clipping stranger-selective.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => { if (!cancelled) measure(); });
    }
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, [mockPhase]);

  // Wrap startMockDraft to reset tutorial on each fresh draft
  const startMockDraft = useCallback(() => {
    try {
      if (localStorage.getItem("mock1-tutorial-complete") !== "true") {
        setTutorialHint("opponents");
      }
    } catch {}
    _startMockDraft();
  }, [_startMockDraft]);

  // Wrap hook functions to also clear roster (cross-hook side-effect)
  const applySettings = useCallback((newSettings, opts) => {
    _applySettings(newSettings, opts);
    setRoster({});
  }, [_applySettings, setRoster]);

  // Auto-save's other half. applySettings CLOSES the modal (it is the commit
  // for the DEFAULT path); a named league saves continuously while the modal
  // stays open, so it needs the same state change without the close. Roster
  // still clears — that is the documented consequence of changing settings,
  // and it is just as true when the change saves itself.
  const applyLiveSettings = useCallback((newSettings) => {
    setSettings(newSettings);
    setRoster({});
  }, [setSettings, setRoster]);

  const loadLeagueFromList = useCallback((league) => {
    _loadLeagueFromList(league);
    setRoster({});
  }, [_loadLeagueFromList, setRoster]);

  // The header dropdown's per-league pencil: make that league current, then
  // open its settings ON THE FORMAT TAB. Two acts, one click — editing a league
  // you are not on has to switch to it first, or you would be editing one
  // league's settings while the app is still priced for another.
  //
  // THE TABS, not the create stepper. The stepper walks someone through a
  // league that does not exist yet; a returning editor came for one knob and
  // knows which tab it is on. Format is the landing tab because it holds the
  // essentials — budget, teams, QB format, keepers — which is what the pencil
  // reads as an offer to change.
  //
  // Declared HERE, below loadLeagueFromList, and not up with the other
  // settings callbacks: a useCallback dependency array is evaluated where it is
  // written, so naming loadLeagueFromList before its own const would throw on
  // first render rather than fail a lint rule.
  const openLeagueForEdit = useCallback((league) => {
    loadLeagueFromList(league);
    setSettingsFormatIntent(true);
    setShowSettings(true);
  }, [loadLeagueFromList, setShowSettings]);

  const loadDefaultPlatform = useCallback((platform) => {
    _loadDefaultPlatform(platform);
    setRoster({});
  }, [_loadDefaultPlatform, setRoster]);


  const handleBuildTeam = useCallback((archetypeName, mods = []) => {
    // Gather locked roster entries (manually placed or locked players)
    const lockedRoster = {};
    rosterSlots.forEach(slot => {
      if (lockedSlots.has(slot.id) && roster[slot.id]) {
        lockedRoster[slot.id] = roster[slot.id];
      }
    });

    const result = buildSmartTeam(
      archetypeName, UNIQUE_PLAYERS, settings, lockedRoster,
      builderGetValue, getPlayerProj, getPlayerVorp, mods
    );

    setSelectedArchetype(archetypeName);
    setRoster(result.roster);
  }, [rosterSlots, lockedSlots, roster, settings, builderGetValue, getPlayerProj, getPlayerVorp]);

  const budgetPct = (spent / BUDGET) * 100;
  const playerCount = UNIQUE_PLAYERS.length;

  // ── Tutorial hint advancement ───────────────────────────────────────────────
  const HINT_SEQ = useMemo(() => ["opponents", "view-toggle", "watchlist", "roster", "tier-sheet", "nominate-player", "set-price", "place-bid", "auto-controls", "speed", "go"], []);
  const advanceHint = useCallback((from) => {
    setTutorialHint(prev => {
      // null is terminal — the tutorial is complete or was skipped. Nothing may
      // advance out of it. (Without this, indexOf(null) === -1 makes the
      // fast-forward guard `fromIdx >= prevIdx` always true, resurrecting the
      // tour on the next nominate/bid.)
      if (prev === null) return null;
      // Scan forward from startIdx for the next step whose target is rendered (or has none).
      const nextStepFrom = (startIdx, prevKey, fromKey) => {
        for (let i = startIdx; i < HINT_SEQ.length; i++) {
          const candidate = HINT_SEQ[i];
          const config = HINT_CONFIG[candidate];
          if (!config?.selector) {
            // No selector (e.g. "go" step) — always valid
            console.log(`[Tutorial] "${prevKey}" → "${candidate}" (triggered by "${fromKey}")`);
            return candidate;
          }
          // Phase-dependent steps: don't skip — DOM target will appear after phase transition
          if (config.waitForDOM) {
            console.log(`[Tutorial] "${prevKey}" → "${candidate}" (triggered by "${fromKey}", waitForDOM)`);
            return candidate;
          }
          const selectors = config.selector.split(",").map(s => s.trim());
          const found = selectors.some(sel => {
            const el = document.querySelector(sel);
            return el && el.getBoundingClientRect().width > 0;
          });
          if (found) {
            console.log(`[Tutorial] "${prevKey}" → "${candidate}" (triggered by "${fromKey}")`);
            return candidate;
          }
          console.log(`[Tutorial] Skipping tutorial step "${candidate}" — target not found`);
        }
        console.log(`[Tutorial] "${prevKey}" → null (no more valid steps)`);
        try { localStorage.setItem("mock1-tutorial-complete", "true"); } catch {}
        return null;
      };

      const prevIdx = HINT_SEQ.indexOf(prev);
      const fromIdx = HINT_SEQ.indexOf(from);

      if (prev !== from) {
        // A later action fired while an earlier hint is still showing (e.g. user
        // clicks Nominate during the very first hint). Fast-forward past `from`
        // instead of freezing the tutorial (and the draft) on the stale hint.
        if (fromIdx >= 0 && fromIdx >= prevIdx) {
          console.log(`[Tutorial] advanceHint("${from}") fast-forwarding from "${prev}"`);
          return nextStepFrom(fromIdx + 1, prev, from);
        }
        console.log(`[Tutorial] advanceHint("${from}") skipped — current hint is "${prev}"`);
        return prev;
      }
      if (prevIdx < 0 || prevIdx >= HINT_SEQ.length - 1) {
        console.log(`[Tutorial] "${prev}" → null (tutorial complete)`);
        try { localStorage.setItem("mock1-tutorial-complete", "true"); } catch {}
        return null;
      }
      return nextStepFrom(prevIdx + 1, prev, from);
    });
  }, [HINT_SEQ]);

  // Reset placeBidReady when entering place-bid step
  useEffect(() => {
    if (tutorialHint === "place-bid") setPlaceBidReady(false);
  }, [tutorialHint]);

  // Freeze place-bid once first AI bid lands (non-user bidder)
  useEffect(() => {
    if (tutorialHint !== "place-bid" || placeBidReady) return;
    if (mockCurrentBidder !== null && mockCurrentBidder !== mockUserSlot) {
      console.log("[Tutorial] AI bid detected (slot", mockCurrentBidder, ") — freezing for place-bid hint");
      setPlaceBidReady(true);
    }
  }, [tutorialHint, placeBidReady, mockCurrentBidder, mockUserSlot]);

  // Landing returns before headerEl exists, so it needs its own banner.
  //
  // suppressed={mockDraftLive}, NOT false. The landing PAGE holds no unsaved
  // state, but "landing is showing" does not imply "no draft in state":
  // onPop (browser Back to "/"), goHome, and AboutPage's onHome all set
  // showLanding(true) without touching the draft. Only the leave-confirm
  // calls resetMockDraft() first. So a mid-draft user who opens the Almanac
  // and hits Back is on landing with a live auction still in memory, and an
  // unsuppressed Reload would destroy it.
  //
  // With no draft in progress — the normal case — mockDraftLive is false and
  // this behaves exactly as an unsuppressed banner would.
  if (showLanding) return <>
    <UpdateBanner suppressed={mockDraftLive} />
    <LandingPage liveBoardEnabled={liveEnabled} rateEnabled={rateEnabled} onOpenLiveBoard={() => {
    setShowLanding(false);
    setAppMode("live");
    // 🔴 ?setup=1 — see SETUP_PARAM in LiveBoard.jsx. This card is the first
    // thing a stranger sees; it must open the setup screen, never somebody's
    // half-finished board from three weeks ago. A bare /live is unchanged.
    window.history.pushState({}, "", "/live?setup=1");
    document.title = "Mock 1 — Live Board";
  }} onLaunch={(mode) => { setShowLanding(false); setAppMode(mode); }} onAbout={() => {
    setShowLanding(false);
    setAppMode("about");
    window.history.pushState({}, "", "/about");
    document.title = "Mock 1 — About";
  }} onOpenAlmanac={() => {
    setRecapStage("landing");
    setRecapPipelineState(null);
    setShowLanding(false);
    setAppMode("recap");
    window.history.pushState({}, "", "/almanac");
    document.title = "Mock 1 — League Almanac";
  }} />
  </>;

  // Shared site chrome — the builder Header, rendered on every non-landing page
  // (Almanac / About / DraftRecap included) for uniform chrome. On those special
  // pages mockDraftActive is forced false and the logo returns home; everything
  // else, the league selector included, is wired exactly as in builder mode —
  // switching leagues there runs the full loadLeagueFromList path (settings,
  // activeLeagueName + its localStorage persist, tier overrides, opponent
  // configs). Popstate routing is untouched.
  const isSpecialPage = appMode === "recap" || appMode === "about" || appMode === "draftrecap";
  const openAlmanacFromHeader = () => {
    setRecapStage("landing");
    setRecapPipelineState(null);
    setShowLanding(false);
    setAppMode("recap");
    window.history.pushState({}, "", "/almanac");
    document.title = "Mock 1 — League Almanac";
  };
  // The Almanac's ESPN signpost points here. SettingsModal is mounted in the
  // BUILDER return, which the recap branch returns before ever reaching — so
  // this cannot just flip showSettings; it has to leave recap mode first.
  // showLanding stays false on purpose: the destination is the app with the
  // modal open, not the marketing page.
  const openSettingsFromAlmanac = () => {
    // ⚠️ SUPPRESS THE TEAM BUILDER INTRO FIRST. It renders at z-index 1100,
    // above SettingsModal's 100, and covers the whole viewport with
    // pointerEvents auto — so a first-time visitor arriving here would see the
    // settings modal and be unable to click it. Fixed by not showing the intro
    // at all rather than by out-stacking it: the two should never co-occur, and
    // a z-index fight would leave the intro half-covered in the case it IS
    // meant for. Someone who clicked "Settings" asked for settings.
    //
    // Written BEFORE setAppMode because OnboardingSheet is keyed on appMode and
    // reads this flag in a useState INITIALIZER — the remount that this mode
    // change triggers is the only read it gets, so the write has to already
    // have happened.
    //
    // Consequence, accepted: this marks the intro seen permanently, exactly as
    // "Don't show again" does, so a user who reaches Settings by this route
    // never sees the Team Builder intro later.
    try { localStorage.setItem(ONBOARDING_BUILDER_KEY, "true"); } catch { /* storage unavailable */ }
    setAppMode("builder");
    setShowLanding(false);
    setShowSettings(true);
    window.history.pushState({}, "", "/");
    document.title = "Mock 1 — Fantasy Auction Draft Simulator";
  };
  const goHome = () => {
    setAppMode("builder");
    setShowLanding(true);
    window.history.pushState({}, "", "/");
    document.title = "Mock 1 — Fantasy Auction Draft Simulator";
  };
  // Header + the stale-bundle banner travel together so both wrapChrome (special
  // pages) and the main return get the banner from one place.
  //
  // suppressed={mockDraftLive} is the load-bearing part: mockDraftLive is
  // phase-based (not setup, not complete) so it is FALSE on the mock setup
  // screen and TRUE even when the user navigates to the Almanac mid-draft —
  // which is right, because the draft still exists in state and a reload would
  // destroy it. mockDraftActive would be wrong here: it also requires
  // appMode === "mock", so it goes false the moment someone browses away from a
  // live draft, exactly when the banner must stay hidden.
  const headerEl = (
    <>
      <UpdateBanner suppressed={mockDraftLive} />
      <Header appMode={appMode} setAppMode={setAppMode} mockDraftActive={isSpecialPage ? false : mockDraftActive} setMockLeaveTarget={setMockLeaveTarget} settings={settings} onLogoClick={isSpecialPage ? goHome : () => mockDraftActive ? setMockLeaveTarget("landing") : setShowLanding(true)} savedLeagues={savedLeagues} activeLeagueName={activeLeagueName} onLoadLeague={loadLeagueFromList} leagueLocked={mockDraftLive} calibrated={activeLeague?.calibrate === true} onOpenSettings={() => setShowSettings(true)} onCreateLeague={openCreateLeague} onEditLeague={openLeagueForEdit} onOpenAlmanac={openAlmanacFromHeader} liveBoardEnabled={liveEnabled} bottomContent={appMode === "builder" ? (
          <>
          <div className="budget-bar-full">
            <div className="budget-info-row">
              <span style={{ color: "#94a3b8", letterSpacing: "1px" }}>BUDGET</span>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                <span style={{ color: "#94a3b8" }}>Spent: <span style={{ color: "#f472b6" }}>${spent}</span></span>
                <span style={{ color: "#94a3b8" }}>Remaining: <span style={{ color: "#34d399" }}>${remaining}</span></span>
                <span style={{ color: "#94a3b8" }}>Max Bid: <span style={{ color: maxBid > 0 ? "#FF9500" : "#ef4444", fontWeight: 700 }}>${Math.max(0, maxBid)}</span></span>
              </div>
            </div>
            <div className="budget-track">
              <div className="budget-bar" style={{
                width: `${Math.min(100, budgetPct)}%`, height: "100%", borderRadius: 8,
                background: budgetPct > 90 ? "linear-gradient(90deg, #ef4444, #dc2626)" : budgetPct > 70 ? "linear-gradient(90deg, #FF6B00, #E55A00)" : "linear-gradient(90deg, #FF6B00, #E55A00)",
                transition: "width 0.3s ease",
                boxShadow: budgetPct > 0 ? `0 0 12px ${budgetPct > 90 ? "rgba(239,68,68,0.4)" : budgetPct > 70 ? "rgba(255,107,0,0.4)" : "rgba(255,107,0,0.4)"}` : "none",
              }} />
            </div>
          </div>
          <div className="budget-bar-compact" style={{ display: "none", flexDirection: "column", gap: 4, marginTop: 8, padding: "6px 12px", background: "rgba(255,255,255,0.03)", borderRadius: 8, fontSize: 11, fontWeight: 600, letterSpacing: "0.5px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
              <span style={{ color: "#94a3b8" }}>Spent: <span style={{ color: "#f472b6" }}>${spent}</span></span>
              <span style={{ color: "#334155" }}>|</span>
              <span style={{ color: "#94a3b8" }}>Left: <span style={{ color: "#34d399" }}>${remaining}</span></span>
              <span style={{ color: "#334155" }}>|</span>
              <span style={{ color: "#94a3b8" }}>Max: <span style={{ color: maxBid > 0 ? "#FF9500" : "#ef4444", fontWeight: 700 }}>${Math.max(0, maxBid)}</span></span>
            </div>
            <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 6, height: 6, overflow: "hidden" }}>
              <div className="budget-bar" style={{
                width: `${Math.min(100, budgetPct)}%`, height: "100%", borderRadius: 6,
                background: budgetPct > 90 ? "linear-gradient(90deg, #ef4444, #dc2626)" : "linear-gradient(90deg, #FF6B00, #E55A00)",
                transition: "width 0.3s ease",
                boxShadow: budgetPct > 0 ? `0 0 12px ${budgetPct > 90 ? "rgba(239,68,68,0.4)" : "rgba(255,107,0,0.4)"}` : "none",
              }} />
            </div>
          </div>
          </>
        ) : /* appMode gate is load-bearing: the phase test alone leaks the controls bar onto every non-builder page whenever a parked live draft exists. */ (appMode === "mock" && mockPhase !== "setup" && mockPhase !== "complete") ? (
          <div className="mock-controls-bar" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
            {/* GROUP 1: Draft Controls — Speed pills + Play/Pause */}
            <div className="mock-speed-clock" style={{
              display: "flex",
              gap: 4,
              alignItems: "center",
              background: "#0d1320",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 8,
              padding: "4px 8px"
            }}>
              <div style={{ display: "flex", gap: 0, background: "#0d1320", borderRadius: 6, border: "1px solid rgba(255,255,255,0.06)", overflow: "hidden" }}>
                {Object.entries(SPEED_SETTINGS).map(([key, s]) => (
                  <button key={key} onClick={() => setMockSpeed(key)} style={{
                    height: 32, padding: "0 10px", fontSize: 10, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit",
                    background: mockSpeed === key ? "linear-gradient(135deg, #FF6B00, #E55A00)" : "transparent",
                    color: mockSpeed === key ? "#fff" : "#64748b", letterSpacing: "0.5px",
                  }}>{s.label}</button>
                ))}
              </div>
            </div>

            {/* Pick counter - plain text */}
            <span className="mock-pick-counter" style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, whiteSpace: "nowrap" }}>
              {mockPickNumber}/{rosterSlots.length * settings.teams}
            </span>

            {/* GROUP 2: User Options — Auto-Bid, Simulate to End, Mute */}
            <div className="mock-main-controls" style={{
              display: "flex",
              gap: 4,
              alignItems: "center",
              background: "#0d1320",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 8,
              padding: "4px 8px"
            }}>
              <button className="btn" onClick={() => setMockAutopick(p => !p)} style={{
                height: 32,
                background: !mockAutopick ? "#FF9500" : "rgba(255,255,255,0.04)",
                color: !mockAutopick ? "#0a0a0a" : "#94a3b8",
                padding: "0 14px",
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 600,
                border: "1px solid rgba(255,255,255,0.08)",
                fontFamily: "inherit",
              }}>{mockAutopick ? "Manual" : "Auto-Bid"}</button>
              <button className="btn" onClick={() => window.confirm("Simulate the rest of the draft to completion?") && autoFinishMock()} style={{
                height: 32, background: "rgba(255,255,255,0.04)",
                color: "#94a3b8", padding: "0 14px", borderRadius: 6,
                fontSize: 11, fontWeight: 600, border: "1px solid rgba(255,255,255,0.08)", fontFamily: "inherit",
              }}>Simulate to End</button>
              <button className="btn" onClick={openAllRosters} title="View all rosters (R)" style={{
                height: 32, background: "rgba(255,255,255,0.04)",
                color: "#94a3b8", padding: "0 14px", borderRadius: 6,
                fontSize: 11, fontWeight: 600, border: "1px solid rgba(255,255,255,0.08)", fontFamily: "inherit",
                display: "flex", alignItems: "center", gap: 6,
              }}>{"📋"} Rosters</button>
              {!mockIsUserTurn && !skipMode && (
                <button className="btn" onClick={startSkip} style={{
                  height: 32, background: "rgba(99,102,241,0.12)",
                  color: "#a78bfa", padding: "0 14px", borderRadius: 6,
                  fontSize: 11, fontWeight: 600, border: "1px solid rgba(99,102,241,0.25)", fontFamily: "inherit",
                }}>Skip to My Pick</button>
              )}
              {skipMode && (
                <button className="btn" onClick={stopSkipMode} style={{
                  height: 32, background: "rgba(239,68,68,0.12)",
                  color: "#f87171", padding: "0 14px", borderRadius: 6,
                  fontSize: 11, fontWeight: 600, border: "1px solid rgba(239,68,68,0.25)", fontFamily: "inherit",
                  animation: "pulse 1.5s ease-in-out infinite",
                }}>Stop Skip</button>
              )}
              <button className="btn mock-mute-btn" onClick={() => setMockMuted(m => !m)} style={{
                height: 32, background: "rgba(255,255,255,0.04)", color: mockMuted ? "#ef4444" : "#64748b",
                padding: "0 10px", borderRadius: 6, fontSize: 14, fontWeight: 700,
                border: "1px solid rgba(255,255,255,0.08)",
                fontFamily: "inherit", lineHeight: 1,
              }}>{mockMuted ? "\uD83D\uDD07" : "\uD83D\uDD0A"}</button>
              {/* Mobile overflow toggle for Recap/Reset */}
              <div className="mock-overflow-toggle" style={{ position: "relative", display: "none" }}>
                <button className="btn" onClick={() => setShowMockOverflow(p => !p)} style={{
                  height: 32, background: showMockOverflow ? "rgba(255,107,0,0.1)" : "rgba(255,255,255,0.04)",
                  color: showMockOverflow ? "#FF9500" : "#94a3b8", padding: "0 10px", borderRadius: 6,
                  fontSize: 14, fontWeight: 700, border: "1px solid rgba(255,255,255,0.08)", fontFamily: "inherit",
                }}>&#8943;</button>
                {showMockOverflow && (
                  <div style={{
                    position: "absolute", top: "100%", right: 0, marginTop: 4, background: "#111827",
                    border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: 8, zIndex: 200,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.5)", display: "flex", flexDirection: "column", gap: 4, minWidth: 120,
                  }}>
                    <button className="btn" onClick={() => { setMockShowRecap(true); setShowMockOverflow(false); }} style={{
                      height: 32, background: "rgba(255,255,255,0.04)", color: "#94a3b8", padding: "0 14px", borderRadius: 6,
                      fontSize: 11, fontWeight: 600, border: "1px solid rgba(255,255,255,0.08)", fontFamily: "inherit", width: "100%",
                    }}>Recap</button>
                    <button className="btn" onClick={() => { resetMockDraft(); setShowMockOverflow(false); }} style={{
                      height: 32, background: "rgba(255,255,255,0.04)", color: "#94a3b8", padding: "0 14px", borderRadius: 6,
                      fontSize: 11, fontWeight: 600, border: "1px solid rgba(255,255,255,0.08)", fontFamily: "inherit", width: "100%",
                    }}>Reset</button>
                  </div>
                )}
              </div>
            </div>

            {/* GROUP 3: Draft Management — Recap, Reset (text-only links) */}
            <div className="mock-mgmt-controls mock-overflow-hide" style={{
              display: "flex",
              gap: 4,
              alignItems: "center",
              background: "#0d1320",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 8,
              padding: "4px 8px"
            }}>
              <button className="btn mock-link-btn" onClick={() => setMockShowRecap(true)} style={{
                height: 32,
                background: "transparent",
                color: "#94a3b8",
                padding: "0 14px",
                borderRadius: 0,
                fontSize: 11,
                fontWeight: 600,
                border: "none",
                fontFamily: "inherit",
                cursor: "pointer",
              }}>Recap</button>
              <button className="btn mock-link-btn" onClick={resetMockDraft} style={{
                height: 32,
                background: "transparent",
                color: "#94a3b8",
                padding: "0 14px",
                borderRadius: 0,
                fontSize: 11,
                fontWeight: 600,
                border: "none",
                fontFamily: "inherit",
                cursor: "pointer",
              }}>Reset</button>
            </div>
          </div>
        ) : null}>
            <div className="header-row">
              {appMode === "builder" ? (
                <>
                  {/* Hamburger toggle — visible only on mobile */}
                  <button className="btn builder-menu-toggle" onClick={() => setShowBuilderMenu(p => !p)} style={{
                    display: "none", alignItems: "center", justifyContent: "center",
                    background: showBuilderMenu ? "rgba(255,107,0,0.15)" : "rgba(255,255,255,0.04)",
                    color: showBuilderMenu ? "#FF9500" : "#94a3b8", padding: "8px 12px", borderRadius: 8,
                    fontSize: 16, fontWeight: 700, border: `1px solid ${showBuilderMenu ? "rgba(255,107,0,0.3)" : "rgba(255,255,255,0.08)"}`, fontFamily: "inherit",
                  }}>&#8943;</button>
                  {/* Full button row — hidden on mobile, shown in overflow menu */}
                  <div className={`builder-menu-buttons${showBuilderMenu ? " open" : ""}`} style={{ display: "contents" }}>
                    <input
                      value={buildName}
                      onChange={e => setBuildName(e.target.value)}
                      placeholder="Build name..."
                      style={{
                        background: "#0d1320", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8,
                        padding: "8px 12px", color: "#e2e8f0", fontSize: 13, width: 140, fontFamily: "inherit",
                      }}
                    />
                    <button className="btn" onClick={() => { saveBuild(); setShowBuilderMenu(false); }} style={{
                      background: "linear-gradient(135deg, #FF6B00, #E55A00)",
                      color: "#fff", padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                      boxShadow: "0 2px 8px rgba(255,107,0,0.3)",
                    }}>Save</button>
                    <button className="btn" onClick={() => { setShowSaved(!showSaved); setShowBuilderMenu(false); }} style={{
                      background: "#1a2332", color: "#e2e8f0", padding: "8px 16px", borderRadius: 8,
                      fontSize: 13, fontWeight: 600, border: "1px solid rgba(255,255,255,0.08)", fontFamily: "inherit",
                    }}>Builds ({savedBuilds.length})</button>
                    <button className="btn" onClick={() => { setShowSettings(true); setShowBuilderMenu(false); }} style={{
                      background: "#1a2332", color: "#e2e8f0", padding: "8px 16px", borderRadius: 8,
                      fontSize: 13, fontWeight: 600, border: "1px solid rgba(255,255,255,0.08)", fontFamily: "inherit",
                    }}>Settings</button>
                    <button className="btn" onClick={() => { clearRoster(); setShowBuilderMenu(false); }} style={{
                      background: "#1a2332", color: "#f87171", padding: "8px 16px", borderRadius: 8,
                      fontSize: 13, fontWeight: 600, border: "1px solid rgba(255,255,255,0.08)", fontFamily: "inherit",
                    }}>Clear</button>
                  </div>
                </>
              ) : null}
            </div>
      </Header>
    </>
  );
  // Stack header + content so the (often full-height) special-page content lays
  // out and scrolls UNDER the shared header.
  const wrapChrome = (content) => (
    <div className="app-root" style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {headerEl}
      <div style={{ flex: 1, minHeight: 0 }}>{content}</div>
    </div>
  );

  if (appMode === "about") {
    return wrapChrome(
      <AboutPage onHome={() => {
        setAppMode("builder");
        setShowLanding(true);
        window.history.pushState({}, "", "/");
        document.title = "Mock 1 — Fantasy Auction Draft Simulator";
      }} />
    );
  }

  if (appMode === "draftrecap") {
    // Draft recaps aren't persisted, so a direct /recap load has nothing to
    // show — empty state pointing to the mock draft. Post-draft ResultsScreen
    // (mock mode) is unaffected.
    return wrapChrome(
      <DraftRecapEmpty onStartDraft={() => {
        setAppMode("mock");
        setShowLanding(false);
        window.history.pushState({}, "", "/");
        document.title = "Mock 1 — Fantasy Auction Draft Simulator";
      }} />
    );
  }

  if (appMode === "recap") {
    if (recapStage === "loading-shared") {
      return wrapChrome(<SharedAlmanacLoading />);
    }
    if (recapStage === "landing") {
      return wrapChrome(
        <RecapLanding
          shareError={shareError}
          onPickPlatform={(platform) => setRecapStage(`importing-${platform}`)}
          onOpenSettings={openSettingsFromAlmanac}
          // JSON re-import lands on the SAME handoff as RecapImport's
          // onPipelineReady — the loader emits an identical pipelineState,
          // so 'ready' needs no knowledge of which door the data came in.
          onAlmanacLoaded={(state) => {
            setRecapPipelineState(state);
            setRecapStage("ready");
          }}
        />
      );
    }
    // ⚠️ SECOND BINARY, SAME BUG CLASS AS THE ONE INSIDE RecapImport. This was
    // an explicit two-value check, so `importing-espn` matched NEITHER and fell
    // through to the 'ready' branch below — rendering EmailGate/RecapReport
    // against a null pipelineState. Derived from the stage prefix now, but
    // validated against RECAP_PLATFORMS (exported by RecapImport, the file that
    // actually dispatches) so an unrecognised stage still cannot reach 'ready'
    // by accident.
    const importingPlatform = recapStage.startsWith("importing-")
      ? recapStage.slice("importing-".length)
      : null;
    if (importingPlatform && RECAP_PLATFORMS.includes(importingPlatform)) {
      const platform = importingPlatform;
      return wrapChrome(
        <RecapImport
          platform={platform}
          onBack={() => setRecapStage("landing")}
          onPipelineReady={(state) => {
            setRecapPipelineState(state);
            setRecapStage("ready");
          }}
        />
      );
    }
    // 'ready' — pipelineState is populated. EmailGate (task #13) shows the
    // preview state until the visitor submits; after unlock, RecapReport
    // (task #14) generates trivia + renders the full report. Share-card
    // affordances land in task #15.
    //
    // 🔴 A SHARED VIEW ROUTES AROUND THE GATE, and the gate itself is
    // untouched — this is a branch above it, not a change inside it. The
    // viewer is not the capture target: they are looking at someone else's
    // league because they were sent a link, and asking them for an email
    // before they can see it costs the entire "no steps at all" promise the
    // link was built to make. The banner below is what converts them instead.
    // Every non-shared path keeps the gate exactly as before.
    if (isSharedView) {
      return wrapChrome(
        <>
          <SharedAlmanacBanner />
          <RecapReport pipelineState={recapPipelineState} />
        </>
      );
    }
    return wrapChrome(
      <EmailGate pipelineState={recapPipelineState}>
        <RecapReport pipelineState={recapPipelineState} />
      </EmailGate>
    );
  }

  return (
    <div className="app-root">

      {/* Header */}
      {headerEl}

      {/* Main Content */}
      {appMode === "builder" ? (
      <div className="main-grid">

        {/* League context — prices, tiers and roster shape are all per-league.
            gridColumn spans both tracks so it reads as a row above the columns
            rather than landing inside the 380px left track. */}
        <LeagueContextLine text="Test out builds for" name={activeLeagueName} style={{ gridColumn: "1 / -1" }}
          onSetup={openSettings} setupSource="team_builder" />

        <OnboardingSheet
          key={appMode}
          storageKey={ONBOARDING_BUILDER_KEY}
          section="Team Builder"
          headline="See what's possible on draft night."
          subtitle="Try different builds at real market prices and see what kind of team you can realistically put together."
        />

        {/* Mobile Tab Toggle */}
        <div className="mobile-tabs">
          <button className="mobile-tab" onClick={() => setMobileTab("players")}
            style={{ background: mobileTab === "players" ? "#FF6B00" : "#1e293b", color: mobileTab === "players" ? "#fff" : "#64748b" }}>
            🏈 PLAYERS ({playerCount - Object.keys(roster).length})
          </button>
          <button className="mobile-tab" onClick={() => setMobileTab("roster")}
            style={{ background: mobileTab === "roster" ? "#FF6B00" : "#1e293b", color: mobileTab === "roster" ? "#fff" : "#64748b" }}>
            📋 ROSTER ({Object.keys(roster).length}/{rosterSlots.length})
          </button>
          <button className="mobile-tab" onClick={() => setMobileTab("budget")}
            style={{ background: mobileTab === "budget" ? "#FF6B00" : "#1e293b", color: mobileTab === "budget" ? "#fff" : "#64748b" }}>
            💰 BUDGET
          </button>
          <button className="mobile-tab" onClick={() => setShowSettings(true)}
            style={{ background: "#1e293b", color: "#94a3b8" }}>
            ⚙️ SETTINGS
          </button>
        </div>


        {/* Left Column: Roster + Budget */}
        <div className="builder-left-col" style={{ minWidth: 0 }}>
        <div className={mobileTab !== "roster" ? "roster-panel-hide" : ""} style={{ minWidth: 0 }}>
          <div className="panel">
            <div className="panel-header">
              <span className="section-label">MY ROSTER</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, color: "#94a3b8" }}>{Object.keys(roster).length}/{rosterSlots.length} filled</span>
                {selectedArchetype && (
                  <button className="btn" onClick={() => handleBuildTeam(selectedArchetype, buildModifiers)} style={{
                    background: "rgba(255,107,0,0.1)", color: "#FF9500",
                    padding: "4px 8px", borderRadius: 6, fontSize: 13,
                    fontWeight: 600, border: "1px solid rgba(255,107,0,0.2)",
                    fontFamily: "inherit", lineHeight: 1,
                  }} title="Shuffle — rebuild with same archetype">🔀</button>
                )}
                <button className="btn" onClick={() => { setShowBuildPanel(prev => { if (!prev) { setBuildStep(0); setBuildModifiers([]); } return !prev; }); }} style={{
                  background: showBuildPanel ? "linear-gradient(135deg, #FF6B00, #E55A00)" : "rgba(255,107,0,0.1)",
                  color: showBuildPanel ? "#fff" : "#FF9500",
                  padding: "4px 10px", borderRadius: 6, fontSize: 11,
                  fontWeight: 600, border: showBuildPanel ? "1px solid #FF6B00" : "1px solid rgba(255,107,0,0.2)",
                  fontFamily: "inherit",
                }}>Quick Build</button>
                {Object.keys(roster).length > 0 && (
                  <button className="btn" onClick={() => {
                    setRoster({});
                    setSelectedArchetype(null);
                    setBuildModifiers([]);
                  }} style={{
                    padding: "4px 10px", borderRadius: 6, fontSize: 11,
                    fontWeight: 600, fontFamily: "inherit", cursor: "pointer",
                    background: "transparent", color: "#94a3b8",
                    border: "1px solid rgba(255,255,255,0.06)",
                  }}>Clear</button>
                )}
                <button className="btn" onClick={() => { setBuildName(""); setShowSaveBuildDialog(true); }}
                  disabled={!rosterHasPlayers}
                  style={{
                    background: rosterHasPlayers ? "rgba(255,107,0,0.1)" : "rgba(255,255,255,0.03)",
                    color: rosterHasPlayers ? "#FF9500" : "#334155",
                    padding: "4px 8px", borderRadius: 6, fontSize: 11,
                    fontWeight: 600, border: "1px solid rgba(255,107,0,0.2)",
                    fontFamily: "inherit", cursor: rosterHasPlayers ? "pointer" : "default",
                    opacity: rosterHasPlayers ? 1 : 0.5,
                  }} title="Save current roster build">💾</button>
                {savedBuilds.length > 0 && (
                  <button className="btn" onClick={() => setShowSaved(true)} style={{
                    background: "rgba(99,102,241,0.1)", color: "#818cf8",
                    padding: "4px 8px", borderRadius: 6, fontSize: 11,
                    fontWeight: 600, border: "1px solid rgba(99,102,241,0.2)",
                    fontFamily: "inherit",
                  }} title="Compare saved builds">📊</button>
                )}
              </div>
            </div>
            {showBuildPanel && (
              <div onClick={() => setShowBuildPanel(false)} style={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
                background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <div onClick={e => e.stopPropagation()} style={{
                  background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14,
                  padding: "24px 20px 20px", width: 380, maxWidth: "92vw", boxShadow: "0 16px 48px rgba(0,0,0,0.8)",
                  maxHeight: "85vh", overflowY: "auto",
                }}>
                  {/* Step dots */}
                  <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 18 }}>
                    {[0, 1, 2].map(i => (
                      <div key={i} style={{
                        width: 8, height: 8, borderRadius: "50%",
                        background: buildStep === i ? "#FF6B00" : buildStep > i ? "rgba(255,107,0,0.4)" : "rgba(255,255,255,0.15)",
                        transition: "background 0.2s ease",
                      }} />
                    ))}
                  </div>

                  {/* Step 0: QB Strategy */}
                  {buildStep === 0 && (
                    <div key="step0" style={{ animation: "wizardSlideIn 0.2s ease" }}>
                      <div style={{ textAlign: "center", marginBottom: 16 }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: "#e2e8f0", fontFamily: fonts.display }}>Pick a QB Strategy</div>
                      </div>
                      {[
                        { emoji: "\uD83D\uDC51", name: "Elite", desc: "Lock in Josh Allen, Lamar, or Burrow", mod: "Elite QB" },
                        { emoji: "\uD83C\uDFAF", name: "Best Available", desc: "Grab a solid starter, mid-range", mod: null },
                        { emoji: "\uD83D\uDDD1\uFE0F", name: "Punt", desc: "Spend $1-5, load up elsewhere", mod: "Punt QB" },
                      ].map(opt => (
                        <button key={opt.name} className="btn" onClick={() => {
                          let next = buildModifiers.filter(m => !m.includes("QB"));
                          if (opt.mod) next.push(opt.mod);
                          setBuildModifiers(next);
                          setBuildStep(1);
                        }} style={{
                          width: "100%", display: "flex", alignItems: "center", gap: 14,
                          padding: "16px 20px", minHeight: 64, marginBottom: 8, borderRadius: 10, cursor: "pointer",
                          background: "#1a2332", border: "1px solid rgba(255,255,255,0.06)",
                          transition: "all 0.15s ease", textAlign: "left",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,107,0,0.4)"; e.currentTarget.style.background = "#1e2a3a"; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.background = "#1a2332"; }}
                        >
                          <span style={{ fontSize: 24, lineHeight: 1, flexShrink: 0 }}>{opt.emoji}</span>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: "#e2e8f0", fontFamily: fonts.display }}>{opt.name}</div>
                            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{opt.desc}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Step 1: TE Strategy */}
                  {buildStep === 1 && (
                    <div key="step1" style={{ animation: "wizardSlideIn 0.2s ease" }}>
                      <div style={{ textAlign: "center", marginBottom: 16 }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: "#e2e8f0", fontFamily: fonts.display }}>Pick a TE Strategy</div>
                      </div>
                      {[
                        { emoji: "\uD83D\uDC51", name: "Elite", desc: "Trey McBride or Brock Bowers", mod: "Elite TE" },
                        { emoji: "\uD83C\uDFAF", name: "Best Available", desc: "Solid starter, mid-range", mod: null },
                        { emoji: "\uD83D\uDDD1\uFE0F", name: "Punt", desc: "$1-3, stream it", mod: "Punt TE" },
                      ].map(opt => (
                        <button key={opt.name} className="btn" onClick={() => {
                          let next = buildModifiers.filter(m => !m.includes("TE"));
                          if (opt.mod) next.push(opt.mod);
                          setBuildModifiers(next);
                          setBuildStep(2);
                        }} style={{
                          width: "100%", display: "flex", alignItems: "center", gap: 14,
                          padding: "16px 20px", minHeight: 64, marginBottom: 8, borderRadius: 10, cursor: "pointer",
                          background: "#1a2332", border: "1px solid rgba(255,255,255,0.06)",
                          transition: "all 0.15s ease", textAlign: "left",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,107,0,0.4)"; e.currentTarget.style.background = "#1e2a3a"; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.background = "#1a2332"; }}
                        >
                          <span style={{ fontSize: 24, lineHeight: 1, flexShrink: 0 }}>{opt.emoji}</span>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: "#e2e8f0", fontFamily: fonts.display }}>{opt.name}</div>
                            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{opt.desc}</div>
                          </div>
                        </button>
                      ))}
                      <button className="btn" onClick={() => setBuildStep(0)} style={{
                        width: "100%", padding: "8px", borderRadius: 8, fontSize: 11, fontWeight: 600,
                        fontFamily: "inherit", background: "transparent", color: "#94a3b8",
                        border: "1px solid rgba(255,255,255,0.06)", cursor: "pointer", marginTop: 4,
                      }}>{"\u2190"} Back</button>
                    </div>
                  )}

                  {/* Step 2: Pick Your Build */}
                  {buildStep === 2 && (() => {
                    const qbMod = buildModifiers.find(m => m.includes("QB"));
                    const teMod = buildModifiers.find(m => m.includes("TE"));
                    const modEmojis = { "Elite QB": "\uD83D\uDC51", "Punt QB": "\uD83D\uDDD1\uFE0F", "Elite TE": "\uD83D\uDC51", "Punt TE": "\uD83D\uDDD1\uFE0F" };
                    const qbLabel = qbMod ? `${modEmojis[qbMod] || ""} ${qbMod}` : "\uD83C\uDFAF Best Avail QB";
                    const teLabel = teMod ? `${modEmojis[teMod] || ""} ${teMod}` : "\uD83C\uDFAF Best Avail TE";
                    const archDescriptions = {
                      "RB Heavy": "Focus on getting great RBs",
                      "WR Heavy": "Focus on getting great WRs",
                      "Balanced": "No preference, best available players",
                      "Stars & Scrubs": "Spend big on 2-3 stars at any position",
                      "Zero RB": "All RBs under $10",
                      "Hero RB": "1 elite RB, then hammer other positions",
                      "The Hammer": "Sits back early, dominates mid-draft",
                    };
                    return (
                      <div key="step2" style={{ animation: "wizardSlideIn 0.2s ease" }}>
                        {/* Selected QB/TE pills */}
                        <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 10 }}>
                          <button className="btn" onClick={() => setBuildStep(0)} style={{
                            padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                            background: "rgba(255,107,0,0.08)", color: "#FF9500",
                            border: "1px solid rgba(255,107,0,0.2)", cursor: "pointer",
                            fontFamily: "inherit", transition: "all 0.15s ease",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,107,0,0.5)"; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,107,0,0.2)"; }}
                          >{qbLabel}</button>
                          <button className="btn" onClick={() => setBuildStep(1)} style={{
                            padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                            background: "rgba(255,107,0,0.08)", color: "#FF9500",
                            border: "1px solid rgba(255,107,0,0.2)", cursor: "pointer",
                            fontFamily: "inherit", transition: "all 0.15s ease",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,107,0,0.5)"; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,107,0,0.2)"; }}
                          >{teLabel}</button>
                        </div>
                        <div style={{ textAlign: "center", marginBottom: 4 }}>
                          <div style={{ fontSize: 18, fontWeight: 800, color: "#e2e8f0", fontFamily: fonts.display }}>Pick Your Build</div>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 12, marginBottom: 12 }}>
                          {Object.keys(ARCHETYPES).map(name => (
                            <button key={name} className="btn" onClick={() => {
                              handleBuildTeam(name, buildModifiers);
                              setShowBuildPanel(false);
                            }} style={{
                              padding: "12px 10px 10px", borderRadius: 10, textAlign: "center",
                              background: selectedArchetype === name ? "rgba(255,107,0,0.12)" : "#1a2332",
                              border: `1px solid ${selectedArchetype === name ? "rgba(255,107,0,0.4)" : "rgba(255,255,255,0.06)"}`,
                              cursor: "pointer", transition: "all 0.12s ease", fontFamily: fonts.display,
                              color: selectedArchetype === name ? "#FF6B00" : "#e2e8f0",
                            }}
                            onMouseEnter={e => { if (selectedArchetype !== name) { e.currentTarget.style.background = "rgba(255,107,0,0.06)"; e.currentTarget.style.borderColor = "rgba(255,107,0,0.2)"; } }}
                            onMouseLeave={e => { if (selectedArchetype !== name) { e.currentTarget.style.background = "#1a2332"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; } }}
                            >
                              <div style={{ fontSize: 13, fontWeight: 700 }}>{name}</div>
                              <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3, fontWeight: 400 }}>{archDescriptions[name] || ""}</div>
                            </button>
                          ))}
                          <button className="btn" onClick={() => {
                            const names = Object.keys(ARCHETYPES);
                            const pick = names[Math.floor(Math.random() * names.length)];
                            handleBuildTeam(pick, buildModifiers);
                            setShowBuildPanel(false);
                          }} style={{
                            padding: "12px 10px 10px", borderRadius: 10, textAlign: "center",
                            background: "rgba(124,58,237,0.08)", cursor: "pointer",
                            border: "1px solid rgba(124,58,237,0.2)",
                            transition: "all 0.12s ease", fontFamily: fonts.display,
                            color: "#a78bfa",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = "rgba(124,58,237,0.15)"; e.currentTarget.style.borderColor = "rgba(124,58,237,0.4)"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "rgba(124,58,237,0.08)"; e.currentTarget.style.borderColor = "rgba(124,58,237,0.2)"; }}
                          >
                            <div style={{ fontSize: 13, fontWeight: 700 }}>{"\uD83C\uDFB2"} Surprise Me</div>
                            <div style={{ fontSize: 10, color: "#7c3aed", marginTop: 3, fontWeight: 400 }}>Random archetype</div>
                          </button>
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button className="btn" onClick={() => setBuildStep(1)} style={{
                            flex: 1, padding: "8px", borderRadius: 8, fontSize: 11, fontWeight: 600,
                            fontFamily: "inherit", background: "transparent", color: "#94a3b8",
                            border: "1px solid rgba(255,255,255,0.06)", cursor: "pointer",
                          }}>{"\u2190"} Back</button>
                          <button className="btn" onClick={() => {
                            setRoster({});
                            setSelectedArchetype(null);
                            setBuildModifiers([]);
                            setShowBuildPanel(false);
                          }} style={{
                            flex: 1, padding: "8px", borderRadius: 8, fontSize: 11,
                            fontWeight: 600, fontFamily: "inherit",
                            background: "rgba(239,68,68,0.05)", color: "#ef4444",
                            border: "1px solid rgba(239,68,68,0.1)", cursor: "pointer",
                          }}>Clear Roster</button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
            <div className="roster-scroll">
              {(() => { let firstEmpty = true; return rosterSlots.map(slot => {
                const player = roster[slot.id];
                const posColor = POS_COLORS[slot.pos];
                const isFirstEmpty = !player && firstEmpty;
                if (isFirstEmpty) firstEmpty = false;
                return (
                  <div key={slot.id} className="slot-row" onClick={!player ? () => {
                    if (slot.pos === "BN") {
                      setPosFilter(new Set(["ALL"]));
                    } else if (slot.pos === "FLEX") {
                      setPosFilter(new Set(["RB", "WR", "TE"]));
                    } else if (slot.pos === "SFLEX") {
                      setPosFilter(new Set(["QB", "RB", "WR", "TE"]));
                    } else {
                      setPosFilter(new Set([slot.pos]));
                    }
                    setSearch("");
                    setTargetSlotId(slot.id);
                    setMobileTab("players");
                    setScrollToAffordable(n => n + 1);
                  } : undefined} style={{
                    display: "flex", alignItems: "center", padding: "5px 14px",
                    borderBottom: "1px solid rgba(255,255,255,0.03)", minHeight: 30, gap: 8, transition: "background 0.15s ease",
                    background: lockedSlots.has(slot.id) ? "rgba(251, 191, 36, 0.04)" : "transparent",
                    cursor: !player ? "pointer" : undefined,
                  }}>
                    <span className="tag" onClick={() => {
                      if (slot.pos === "BN") {
                        setPosFilter(new Set(["ALL"]));
                      } else if (slot.pos === "FLEX") {
                        setPosFilter(new Set(["RB", "WR", "TE"]));
                      } else if (slot.pos === "SFLEX") {
                        setPosFilter(new Set(["QB", "RB", "WR", "TE"]));
                      } else {
                        setPosFilter(new Set([slot.pos]));
                      }
                      setSearch("");
                      if (!roster[slot.id]) setTargetSlotId(slot.id);
                      setMobileTab("players");
                      setScrollToAffordable(n => n + 1);
                    }} style={{ background: posColor.bg, color: posColor.text, minWidth: 38, fontSize: 10, cursor: "pointer", transition: "transform 0.1s ease", padding: "2px 5px" }}
                      title={`Show ${slot.pos === "BN" ? "all" : slot.pos} players`}
                      onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
                      onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                    >
                      {slot.label.replace(/\d+/, '')}
                    </span>
                    {player ? (
                      <>
                        <RosterAvatar player={player} size={22} />
                        {slot.pos === "BN" && <span style={{ fontSize: 9, fontWeight: 700, color: POS_COLORS[player.pos]?.text || "#94a3b8", background: POS_COLORS[player.pos]?.bg || "#334155", padding: "1px 4px", borderRadius: 3, letterSpacing: "0.5px", flexShrink: 0 }}>{player.pos}</span>}
                        <span style={{ flex: 1, fontSize: 12, fontWeight: 500, animation: "fadeInUp 0.25s ease-out" }}>
                          {player.name}<span style={{ color: "#94a3b8", marginLeft: 4, fontSize: 10 }}>{player.team}</span>
                          {BYE_WEEKS_2026[player.team] && <span style={{ color: "#64748b", marginLeft: 4, fontSize: 10, fontWeight: 600 }} title="Bye week">B{BYE_WEEKS_2026[player.team]}</span>}
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#60a5fa", minWidth: 32, textAlign: "right" }}>{(getPlayerProj(player) / getPlayerGames(player)).toFixed(2)}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#FF9500", minWidth: 32, textAlign: "right" }}>${builderGetValue(player)}</span>
                        <button onClick={() => toggleLock(slot.id)} style={{
                          background: "none", border: "none", cursor: "pointer", padding: "0 2px",
                          color: lockedSlots.has(slot.id) ? "#FF9500" : "rgba(255,255,255,0.15)",
                          fontSize: 11, lineHeight: 1, transition: "color 0.15s ease",
                        }} title={lockedSlots.has(slot.id) ? "Unlock" : "Lock"}>
                          {lockedSlots.has(slot.id) ? "\u{1F512}" : "\u{1F513}"}
                        </button>
                        <button className="remove-btn btn" onClick={() => removePlayer(slot.id)} style={{ background: "none", color: "#ef4444", fontSize: 13, lineHeight: 1, padding: "0 2px" }}>×</button>
                      </>
                    ) : (
                      <span style={{ flex: 1, fontSize: isFirstEmpty ? 11 : 12, color: isFirstEmpty ? "#94a3b8" : "#334155", fontStyle: isFirstEmpty ? "italic" : "normal", padding: "2px 0" }}>
                        {isFirstEmpty ? `${isTouch ? "Tap" : "Click"} to add a player` : "—"}
                      </span>
                    )}
                  </div>
                );
              }); })()}
            </div>
            {/* Roster Totals */}
            <div style={{ padding: "4px 12px", borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", gap: 10, fontSize: 10, fontWeight: 700 }}>
                <span style={{ color: "#60a5fa" }}>Wk1: {week1Projection > 0 ? week1Projection : "—"}<span style={{ color: "#94a3b8", fontWeight: 500 }}> proj</span></span>
                <span style={{ color: "#34d399" }}>{starterProjectedTotal > 0 ? starterProjectedTotal.toFixed(2) : "—"}<span style={{ color: "#94a3b8", fontWeight: 500 }}> yr</span></span>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8" }}>{projectedTotal > 0 ? Math.round(projectedTotal).toLocaleString() : "—"} total</span>
            </div>
          </div>
        </div>

        {/* Spending Breakdown */}
        <div className={mobileTab !== "budget" ? "budget-panel-hide" : ""} style={{ minWidth: 0 }}>
          <div className="panel">
            <div className="panel-header">
              <span className="section-label">BUDGET</span>
              <span style={{ fontSize: 11, color: "#94a3b8" }}>
                {selectedArchetype ? (
                  <>{selectedArchetype}{buildModifiers.length > 0 && <span style={{ color: "#FF9500" }}> + {buildModifiers.join(", ")}</span>}</>
                ) : "No archetype"} &middot; ${spent} spent
              </span>
            </div>

              <div style={{ padding: "10px 14px" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {["QB", "RB", "WR", "TE", ...(settings.positions.FLEX > 0 ? ["FLEX"] : []), ...(settings.positions.SFLEX > 0 ? ["SFLEX"] : []), "DEF", "K", ...(settings.positions.BN > 0 ? ["BN"] : [])].map(pos => {
                    const amount = spendingByPos[pos] || 0;
                    const pct = spent > 0 ? (amount / spent) * 100 : 0;
                    return (
                      <div key={pos} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span className="tag" style={{ background: POS_COLORS[pos].bg, color: "#fff", minWidth: 32, fontSize: 9, textAlign: "center", padding: "1px 4px" }}>{pos}</span>
                        <div style={{ flex: 1, background: "#1e293b", borderRadius: 3, height: 7, overflow: "hidden" }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: POS_COLORS[pos].bg, borderRadius: 3, transition: "width 0.3s ease" }} />
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#e2e8f0", minWidth: 38, textAlign: "right" }}>${amount}</span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, letterSpacing: "0.5px" }}>YEAR LONG STARTER PROJECTION</span>
                    <span style={{ fontWeight: 800, color: "#34d399", fontSize: 15 }}>{starterProjectedTotal > 0 ? starterProjectedTotal.toFixed(2) : "—"} pts</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, letterSpacing: "0.5px" }}>WEEK 1 PROJECTION</span>
                    <span style={{ fontWeight: 800, color: "#60a5fa", fontSize: 15 }}>{week1Projection > 0 ? week1Projection : "—"} pts</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, letterSpacing: "0.5px" }}>FULL ROSTER</span>
                    <span style={{ fontWeight: 700, color: "#94a3b8", fontSize: 13 }}>{projectedTotal > 0 ? Math.round(projectedTotal).toLocaleString() : "—"} pts</span>
                  </div>
                </div>
              </div>
          </div>
        </div>

        </div>

        {/* Right: Player Pool */}
        <PlayerList
          renderMode="builder"
          players={filteredPlayers}
          search={search}
          onSearchChange={setSearch}
          posFilter={posFilter}
          onToggleFilter={togglePosFilter}
          isFilterActive={isFilterActive}
          positions={["ALL", "QB", "RB", "WR", "TE", "FLEX", ...(settings.positions.SFLEX > 0 ? ["SFLEX"] : []), ...(settings.positions.DEF > 0 ? ["DEF"] : []), ...(settings.positions.K > 0 ? ["K"] : [])]}
          sortBy={sortBy}
          onSortChange={setSortBy}
          getPlayerValue={builderGetValue}
          getPlayerProj={getPlayerProj}
          getPlayerGames={getPlayerGames}
          getPlayerVorp={getPlayerVorp}
          customStats={customStats}
          getPlayerStats={getPlayerStats}
          getAvailableSlots={getAvailableSlots}
          maxBid={maxBid}
          emptySlots={emptySlots}
          onAddToRoster={handlePlayerClick}
          expandedPlayer={expandedPlayer}
          setExpandedPlayer={setExpandedPlayer}
          mobileTab={mobileTab}
          scoring={settings.scoring}
          scrollToAffordable={scrollToAffordable}
          activeLeagueName={activeLeagueName}
          watchlist={mockWatchlist}
          onToggleWatchlist={toggleWatchlist}
          avoids={myAvoids}
          onToggleAvoid={toggleAvoid}
        />
      </div>
      ) : appMode === "plan" ? (
      /* ===== DRAFT PLAN MODE ===== */
      /* Not in isSpecialPage: the plan wants the normal app header. It carries
         its own summary bar, so the header's bottomContent stays builder-only. */
      <DraftPlan
        settings={settings}
        rosterSlots={rosterSlots}
        dynamicPrices={dynamicPrices}
        plan={planApi.activePlan}
        planApi={planApi}
        activeLeagueName={activeLeagueName}
        getPlayerProj={getPlayerProj}
        getPlayerGames={getPlayerGames}
        getPlayerVorp={getPlayerVorp}
      />
      ) : appMode === "tiers" ? (
      /* ===== TIERS MODE ===== */
      /* Boundary contains a rare dnd-kit measure→setState loop (React #185) that
         can fire under heavy cross-tier dragging: it degrades to a local reset
         here instead of white-screening the whole app. */
      <ErrorBoundary
        label="tiers"
        renderFallback={(reset) => (
          <div style={{ maxWidth: 480, margin: "80px auto", padding: 24, textAlign: "center" }}>
            <div style={{ fontSize: 15, color: "#e2e8f0", marginBottom: 14 }}>Tiers hit a snag.</div>
            <button
              onClick={reset}
              style={{ background: "linear-gradient(135deg,#FF6B00,#E55A00)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontSize: 14, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}
            >
              Reload tiers
            </button>
          </div>
        )}
      >
        <OnboardingSheet
          key={appMode}
          storageKey="onboarding-tiers"
          section="Tier Sheet"
          headline="Rank similar players into tiers."
          subtitle="Group players you value equally into a clean draft-day cheat sheet. Start from expert tiers — then drag players to make them yours."
        />
        {/* watchlist/avoids are display-only here — TierBuilder renders badges
            but never toggles them (the pill is a drag handle). */}
        <TierBuilder players={UNIQUE_PLAYERS} settings={settings} activeLeagueName={activeLeagueName}
          watchlist={mockWatchlist} avoids={myAvoids} onOpenSettings={openSettings} />
      </ErrorBoundary>
      ) : appMode === "projections" ? (
      /* ===== PROJECTION LAB MODE ===== */
        <ProjectionLab onBack={() => setShowLanding(true)} settings={settings} activeLeagueName={activeLeagueName} />
      ) : appMode === "live" ? (
      /* ===== LIVE BOARD MODE ===== */
      /* Boundary because this one runs during a real auction with eleven people
         watching: a crash here is unrecoverable in a way a broken mock is not.
         The board itself is already safe on disk, so the fallback only has to
         get the component re-mounted. */
      <ErrorBoundary
        label="live"
        renderFallback={(reset) => (
          <div style={{ maxWidth: 480, margin: "80px auto", padding: 24, textAlign: "center" }}>
            <div style={{ fontSize: 15, color: "#e2e8f0", marginBottom: 8 }}>The Live Board hit a snag.</div>
            <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 14 }}>
              Your entered picks are saved on this device and will come back.
            </div>
            <button
              onClick={reset}
              style={{ background: "linear-gradient(135deg,#FF6B00,#E55A00)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontSize: 14, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}
            >
              Reload the board
            </button>
          </div>
        )}
      >
        <LiveBoard
          live={live} dynamicPrices={dynamicPrices} activeLeagueName={activeLeagueName}
          priceSettings={{ budget: settings?.budget, teams: settings?.teams }}
          // The board menu's escape hatch — same destination the header tab
          // goes to. It matters because chunk 5 hides that header at TV, and
          // Ben mirrors his laptop, so the header is not a way out there.
          onLeave={() => setAppMode("builder")}
          // 🔴 FOR THE RECOVERY NOTICE ON THE SETUP SCREEN ONLY. A board in
          // progress can be sitting under a different league name while the
          // Live Board renders a blank setup form, which from the operator's
          // chair is indistinguishable from "my board is gone".
          //
          // ⚠️ THE FULL LEAGUE LOAD, NOT A BARE setActiveLeagueName. Settings,
          // pricing, tier overrides and opponent configs all key off the active
          // league; setting the name alone would open the right board priced
          // for the wrong league. Same path the header's league selector uses.
          savedLeagues={savedLeagues}
          onSwitchLeague={(name) => {
            const league = savedLeagues.find(l => l?.name === name);
            if (league) loadLeagueFromList(league);
          }}
        />
      </ErrorBoundary>
      ) : (
      /* ===== MOCK DRAFT MODE ===== */
      <div className="mock-page" style={{ maxWidth: 1400, margin: "0 auto", padding: "20px 28px", minHeight: "calc(100vh - 140px)", position: "relative" }}>
        {/* Skip-to-my-pick stop reason toast */}
        {skipStopReason && (
          <div style={{
            position: "fixed", top: 72, left: "50%", transform: "translateX(-50%)", zIndex: 9999,
            background: "linear-gradient(135deg, #1e1b4b, #312e81)", border: "1.5px solid rgba(139,92,246,0.5)",
            borderRadius: 12, padding: "10px 24px", boxShadow: "0 8px 32px rgba(99,102,241,0.4)",
            display: "flex", alignItems: "center", gap: 10, animation: "fadeIn 0.2s ease-out",
          }}>
            <span style={{ fontSize: 18 }}>{
              skipStopReason.includes("Watchlist") ? "\uD83C\uDFAF" :
              skipStopReason.includes("Must-fill") ? "\u26A0\uFE0F" : "\uD83D\uDCE2"
            }</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#e0e7ff", letterSpacing: "0.3px" }}>{skipStopReason}</span>
          </div>
        )}

        <OnboardingSheet
          key={appMode}
          storageKey="onboarding-mock"
          section="Mock Draft"
          headline="Mock draft against opponents that actually bid like real people."
          subtitle="Every team drafts differently — stars & scrubs, balanced builds, patient spenders. Calibrated from real auction data."
        />

        {/* ---- SETUP PHASE ---- */}
        {mockPhase === "setup" && (
          <MockSetup
            positionDials={settings.positionDials}
            onSetPositionDials={setPositionDials}
            dialsOverrideLeague={dialsOverrideLeague}
            marketAnchor={settings.marketAnchor}
            onSetMarketAnchor={setMarketAnchor}
            marketAnchorEcho={marketAnchor}
            mockTeamConfigs={mockTeamConfigs}
            setMockTeamConfigs={setMockTeamConfigs}
            mockUserOwner={mockUserOwner}
            setMockUserOwner={setMockUserOwner}
            mockUserSlot={mockUserSlot}
            setMockUserSlot={setMockUserSlot}
            mockSpeed={mockSpeed}
            setMockSpeed={setMockSpeed}
            mockNomTimerMode={mockNomTimerMode}
            setMockNomTimerMode={setMockNomTimerMode}
            startMockDraft={startMockDraft}
            onSimulate={(simulateModifiers) => {
              // Quick Sim never enters the live loop (no startMockDraft), so it
              // fires both funnel events itself.
              captureMockDraftStarted({
                teams: settings.teams,
                budget: settings.budget,
                // Literal SFLEX slot count — this dimension's meaning must stay
                // what it has always been. `format` carries the 2QB split.
                isSflex: (settings.positions.SFLEX || 0) > 0,
                format: leagueFormatLabel(settings.positions),
                positions: settings.positions,
                scoring: settings.scoring,
                // Same pure function MockSetup lights the chip from.
                anchorPreset: marketAnchorPresetFor(settings.marketAnchor, settings.positions, settings.budget),
              });
              const teams = buildDraftTeams(mockUserOwner, mockUserSlot, effectiveTeamConfigs, settings.teams, settings.budget, userTierOverrides, false, settings);
              const result = simulateDraftSync(teams, 1, 0, new Set(), [], mockUserSlot, settings.positions.SFLEX || 0, settings, dynamicPrices, null, rosterSlots.length || MOCK_ROSTER_SIZE);
              captureMockDraftCompleted({ picks: result.draftLog.length, viaSimulate: true, durationSec: 0 });
              setMockTeams(result.teams);
              setMockDraftLog(result.draftLog);
              setMockDraftedPlayers(result.draftedPlayers);
              setMockPickNumber(result.pickNumber);
              setMockCurrentPlayer(null);
              setMockCurrentBid(1);
              setMockCurrentBidder(null);
              setMockActiveBidders(new Set());
              setMockSoldPlayer(null);
              setMockGoingPhase(null);
              setMockAutopick(true);
              setUserSeatAiDriven(true); // Quick Sim drafts the user's seat with their profile
              setMockPhase("complete");
              localStorage.removeItem("auctionArchitect_draftState");
            }}
            onOpenSettings={() => setShowSettings(true)}
            onCreateLeague={openCreateLeague}
            mockSavedDraft={mockSavedDraft}
            onClearSavedDraft={() => { localStorage.removeItem("auctionArchitect_draftState"); setMockSavedDraft(null); }}
            resumeMockDraft={resumeMockDraft}
            settings={settings}
            teamCount={settings.teams}
            activeLeagueName={activeLeagueName}
            activeLeague={activeLeague}
            onSetCalibrate={(on) => updateActiveLeague({ calibrate: on })}
            modeledSeatCount={modeledSeatCount}
          />
        )}

        {/* ---- ACTIVE DRAFT (nominating / bidding / sold) ---- */}
        {(mockPhase === "nominating" || mockPhase === "bidding" || mockPhase === "sold") && (
          <div ref={mockDraftWrapperRef} className={`mobile-auction-active${mockExpandedTeam !== null ? " mob-roster-open" : ""}`}>
            <GuidedTutorial currentHint={(tutorialHint === "place-bid" && !placeBidReady) ? null : tutorialHint} advanceHint={advanceHint} active={mockPhase === "nominating" || mockPhase === "bidding" || mockPhase === "sold"} onSkip={() => setTutorialHint(null)} />
            {/* MOBILE ROW 1: Pause/Resume with pick counter + speed toggle */}
            {mockSpeedToast && <div className="mob-speed-toast-bar">{mockSpeedToast}</div>}
            <TeamGrid teams={mockTeams} userSlot={mockUserSlot} currentBidder={mockCurrentBidder} nominatorIndex={mockNominatorIndex} mockPhase={mockPhase} expandedTeam={mockExpandedTeam} setExpandedTeam={setMockExpandedTeam} draftLog={mockDraftLog} teamCount={settings.teams} rosterSlots={rosterSlots} startingBudget={settings.budget} />
            {/* Mock Mobile Tabs (legacy — hidden on mobile, replaced by split view) */}
            <div className="mock-mobile-tabs" style={{ display: "none", gap: 0, background: "#0d1320", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, overflow: "hidden", marginBottom: 12 }}>
              {[["players", "PLAYERS"], ["auction", "AUCTION"], ["results", "RESULTS"]].map(([key, label]) => (
                <button key={key} className="mobile-tab" onClick={() => setMockMobileTab(key)}
                  style={{ flex: 1, padding: 10, textAlign: "center", fontSize: 11, fontWeight: 700, letterSpacing: "1.5px",
                    cursor: "pointer", border: "none", fontFamily: "inherit",
                    background: mockMobileTab === key ? "linear-gradient(135deg, #FF6B00, #E55A00)" : "transparent",
                    color: mockMobileTab === key ? "#000" : "#64748b",
                  }}>{label}</button>
              ))}
            </div>

            {/* 3-Column Layout (desktop) / Split Layout (mobile) */}
            <div className="mock-split-container" style={{ display: "flex", gap: 16 }}>

              {/* Mobile Bottom Toggle (Players/Results) — hidden on desktop */}
              <div className="mock-bottom-toggle" style={{ display: "none", gap: 0, background: "#0d1320", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, overflow: "hidden", flexShrink: 0 }}>
                {[["players", "Players"], ["results", "Results"]].map(([key, label]) => (
                  <button key={key} onClick={() => setMockMobileTab(key)}
                    style={{ flex: 1, padding: "6px 0", textAlign: "center", fontSize: 10, fontWeight: 700, letterSpacing: "1px",
                      cursor: "pointer", border: "none", fontFamily: "inherit",
                      background: mockMobileTab === key ? "linear-gradient(135deg, #FF6B00, #E55A00)" : "transparent",
                      color: mockMobileTab === key ? "#fff" : "#64748b",
                    }}>{label}</button>
                ))}
              </div>

              {/* Mobile-only Players/Tiers toggle tabs */}
              <div className="mob-panel-tabs" style={{ display: "none" }}>
                {[["players", "Players"], ["tiers", "Tiers"]].map(([key, label]) => (
                  <button key={key} onClick={() => setMobPanelTab(key)} style={{
                    flex: 1, padding: "6px 0", fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                    border: "none", cursor: "pointer", borderRadius: 6,
                    background: mobPanelTab === key ? "#FF6B00" : "rgba(255,255,255,0.04)",
                    color: mobPanelTab === key ? "#000" : "#94a3b8",
                  }}>{label}</button>
                ))}
              </div>

              {/* LEFT: Available Players (hidden on mobile when Tiers tab active) */}
              <PlayerList
                renderMode="auction"
                players={mockAvailablePlayers}
                search={mockSearch}
                onSearchChange={setMockSearch}
                nomGlow={mockNomGlow}
                posFilter={mockPosFilter}
                onToggleFilter={(pos) => {
                  // Single-select: a position shows only that position; clicking
                  // the active one again (or ALL) restores the full list.
                  setMockPosFilter(prev =>
                    (pos === "ALL" || prev.has(pos)) ? new Set(["ALL"]) : new Set([pos])
                  );
                }}
                positions={["ALL", "QB", "RB", "WR", "TE", "FLEX", "DEF", "K", "★"]}
                watchlist={mockWatchlist}
                onToggleWatchlist={toggleWatchlist}
                avoids={myAvoids}
                onToggleAvoid={toggleAvoid}
                canNominate={mockPhase === "nominating"}
                isUserTurn={mockIsUserTurn}
                mockAutopick={mockAutopick}
                nominatingPlayer={mockNominatingPlayer}
                onSetNominatingPlayer={(p) => { setMockNominatingPlayer(p); if (p) { console.log("[Tutorial] trigger: player selected →", p.name); advanceHint("nominate-player"); } }}
                openingBid={mockOpeningBid}
                onOpeningBidChange={setMockOpeningBid}
                userMaxBid={mockUserMaxBid}
                onNominatePlayer={(...args) => { console.log("[Tutorial] trigger: nominate clicked"); mockNominatePlayer(...args); advanceHint("set-price"); }}
                nomTimer={mockNomTimer}
                nomTimerMax={getNomSeconds()}
                mockSpeed={mockSpeed}
                mockMobileTab={mockMobileTab}
                tutorialHint={tutorialHint}
                mobPanelTab={mobPanelTab}
                activeLeagueName={activeLeagueName}
                settings={settings}
                dynamicPrices={dynamicPrices}
              />

              {/* Mobile-only Tier Viewer (shown when Tiers tab active) */}
              <div className="mob-tier-viewer" style={{ display: "none" }}>
                {mobPanelTab === "tiers" && (
                  <TierViewer
                    players={UNIQUE_PLAYERS}
                    currentPlayer={mockCurrentPlayer}
                    draftPicks={mockTierViewerPicks}
                    activeLeagueName={activeLeagueName}
                    watchlist={mockWatchlist}
                    onToggleWatchlist={toggleWatchlist}
                    avoids={myAvoids}
                    onToggleAvoid={toggleAvoid}
                    dynamicPrices={dynamicPrices}
                  />
                )}
              </div>

              {/* CENTER: Active Auction + Tier Viewer */}
              <div className="mock-center-col mock-split-top" style={{ flex: 1, minWidth: 0 }}>
              <AuctionCard
                mockPhase={mockPhase}
                mockCurrentPlayer={mockCurrentPlayer}
                mockCurrentBid={mockCurrentBid}
                mockCurrentBidder={mockCurrentBidder}
                mockActiveBidders={mockActiveBidders}
                userPassed={mockUserPassedRef.current}
                heartbeatPulse={heartbeatPulse}
                mockGoingPhase={mockGoingPhase}
                mockPaused={mockPaused}
                mockSpeed={mockSpeed}
                mockUserSlot={mockUserSlot}
                mockUserMaxBid={mockUserMaxBid}
                mockAutopick={mockAutopick}
                mockPlaceBid={(...args) => { console.log("[Tutorial] trigger: bid placed"); mockPlaceBid(...args); advanceHint("place-bid"); }}
                mockPassBid={(...args) => { console.log("[Tutorial] trigger: pass clicked"); mockPassBid(...args); advanceHint("place-bid"); }}
                onRejoinBidding={() => { clearHandoffPause(); mockUserPassedRef.current = false; SoundEngine.stopFastForward(); setMockActiveBidders(prev => { const next = new Set(prev); next.add(mockUserSlot); return next; }); }}
                mockSoldPlayer={mockSoldPlayer}
                mockWatchlistAlert={mockWatchlistAlert}
                watchlist={mockWatchlist}
                avoids={myAvoids}
                mockNominatorIndex={mockNominatorIndex}
                teams={mockTeams}
                isUserTurn={mockIsUserTurn}
                nominatingPlayer={mockNominatingPlayer}
                openingBid={mockOpeningBid}
                onOpeningBidChange={setMockOpeningBid}
                onNominatePlayer={(...args) => { console.log("[Tutorial] trigger: nominate clicked"); mockNominatePlayer(...args); advanceHint("set-price"); }}
                onCancelNomination={() => setMockNominatingPlayer(null)}
                mockNomTimer={mockNomTimer}
                mockNomTimerMax={getNomSeconds()}
                teamCount={settings.teams}
                mockPaused={mockPaused}
                toggleMockPaused={toggleMockPaused}
                mockPickNumber={mockPickNumber}
                totalPicks={rosterSlots.length * settings.teams}
                mockSpeed={mockSpeed}
                onSpeedChange={(next) => {
                  setMockSpeed(next);
                  setMockSpeedToast(SPEED_SETTINGS[next].label);
                  clearTimeout(mockSpeedToastRef.current);
                  mockSpeedToastRef.current = setTimeout(() => setMockSpeedToast(null), 1500);
                }}
                onToggleAutopick={() => setMockAutopick(p => !p)}
                onSimulateToEnd={() => window.confirm("Simulate the rest of the draft to completion?") && autoFinishMock()}
                onSkip={!mockIsUserTurn && !skipMode ? startSkip : undefined}
                onStopSkip={skipMode ? stopSkipMode : undefined}
                skipMode={skipMode}
                onOpenRosters={openAllRosters}
                dynamicPrices={dynamicPrices}
              />
              {/* Tier Viewer — desktop only */}
              <div className="mock-tier-viewer-desktop" style={{ marginTop: 12 }}>
                <TierViewer
                  players={UNIQUE_PLAYERS}
                  currentPlayer={mockCurrentPlayer}
                  draftPicks={mockTierViewerPicks}
                  activeLeagueName={activeLeagueName}
                  watchlist={mockWatchlist}
                  onToggleWatchlist={toggleWatchlist}
                  avoids={myAvoids}
                  onToggleAvoid={toggleAvoid}
                  dynamicPrices={dynamicPrices}
                />
              </div>
              </div>

              {/* RIGHT: User Roster + Draft Log */}
              {/* Width lives in styles/auction.css (.mock-roster-panel), NOT here —
                  the middle layout tier shrinks it to 260px via media query and an
                  inline width would outrank it. */}
              <div className={`mock-panel mock-roster-panel${mockMobileTab !== "results" ? " mock-results-hide" : ""}`} style={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 12 }}>
                {/* User's Roster — full slot template derived from league settings */}
                {(() => {
                  const slotDefs = rosterSlots;
                  const filled = {};
                  const posQueues = {};
                  (mockUserTeam?.roster || []).forEach(pick => {
                    const pos = pick.player.pos;
                    if (!posQueues[pos]) posQueues[pos] = [];
                    posQueues[pos].push(pick);
                  });
                  Object.values(posQueues).forEach(q => q.sort((a, b) => b.price - a.price));
                  slotDefs.forEach(slot => {
                    if (slot.pos !== "FLEX" && slot.pos !== "BN" && slot.pos !== "SFLEX") {
                      const q = posQueues[slot.pos];
                      if (q && q.length > 0) filled[slot.id] = q.shift();
                    }
                  });
                  const userFlexOrder = (() => {
                    const fp = mockUserTeam?.profile?.flexPos;
                    if (fp === "WR") return ["WR", "RB", "TE"];
                    if (fp === "RB") return ["RB", "WR", "TE"];
                    return ["RB", "WR", "TE"];
                  })();
                  // QB first — a spare QB can ONLY start in SFLEX; skill players are the fallback.
                  slotDefs.filter(s => s.pos === "SFLEX").forEach(slot => {
                    for (const pos of ["QB", ...userFlexOrder]) {
                      if (posQueues[pos] && posQueues[pos].length > 0) { filled[slot.id] = posQueues[pos].shift(); break; }
                    }
                  });
                  slotDefs.filter(s => s.pos === "FLEX").forEach(slot => {
                    for (const pos of userFlexOrder) {
                      if (posQueues[pos] && posQueues[pos].length > 0) { filled[slot.id] = posQueues[pos].shift(); break; }
                    }
                  });
                  slotDefs.filter(s => s.pos === "BN").forEach(slot => {
                    for (const pos of ["RB", "WR", "QB", "TE", "DEF", "K"]) {
                      if (posQueues[pos] && posQueues[pos].length > 0) { filled[slot.id] = posQueues[pos].shift(); break; }
                    }
                  });
                  return (
                    <div className="mock-roster-box" style={{ background: "#0d1320", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, overflow: "hidden" }}>
                      <div style={{ padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.04)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#FF6B00" }}>YOUR ROSTER</span>
                        <span style={{ fontSize: 11, color: "#94a3b8" }}>
                          ${mockUserTeam?.budget || 0} left • {mockUserTeam?.roster.length || 0}/{rosterSlots.length}
                        </span>
                      </div>
                      <div className="mock-roster-slots" style={{ maxHeight: 340, overflowY: "auto" }}>
                        {slotDefs.map((slot, i) => {
                          const pick = filled[slot.id];
                          const posColor = POS_COLORS[slot.pos];
                          const slotLabel = slot.label.replace(/(\d+)/, ' $1');
                          return (
                            <div key={slot.id} className={pick ? "fade-in" : ""} style={{
                              display: "flex", alignItems: "center", gap: 6, padding: "4px 14px",
                              borderBottom: "1px solid rgba(255,255,255,0.03)",
                              opacity: pick ? 1 : 0.45,
                            }}>
                              <span className="tag" style={{
                                background: pick ? posColor.bg : "transparent",
                                color: pick ? posColor.text : posColor.bg,
                                fontSize: 9, padding: "1px 5px", minWidth: 34, textAlign: "center",
                                border: pick ? "none" : `1px dashed ${posColor.bg}`,
                              }}>{slotLabel}</span>
                              {pick ? (
                                <>
                                  <span style={{ flex: 1, fontSize: 11, fontWeight: 500, color: "#e2e8f0" }}>{pick.player.name}</span>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: "#FF9500" }}>${pick.price}</span>
                                </>
                              ) : (
                                <span style={{ flex: 1, fontSize: 10, color: "#334155", letterSpacing: "0.5px" }}>{"\u2014"}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Draft Log — compact, newest first */}
                <div className="mock-draft-log-box" style={{ background: "#0d1320", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, overflow: "hidden", flex: 1 }}>
                  <div style={{ padding: "8px 14px", borderBottom: "1px solid rgba(255,255,255,0.04)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8" }}>DRAFT LOG</span>
                    {mockDraftLog.length > 0 && <span style={{ fontSize: 10, color: "#94a3b8" }}>{mockDraftLog.length} picks</span>}
                  </div>
                  <div className="mock-draft-log-list" style={{ maxHeight: 480, overflowY: "auto" }}>
                    {mockDraftLog.length === 0 ? (
                      <div style={{ padding: 16, textAlign: "center", fontSize: 11, color: "#334155", fontStyle: "italic" }}>Waiting for first pick...</div>
                    ) : (
                      [...mockDraftLog].reverse().map((log, i) => {
                        const posColor = POS_COLORS[log.player.pos];
                        return (
                          <div key={log.pickNumber} className={i === 0 ? "fade-in" : ""} style={{
                            display: "flex", alignItems: "center", gap: 5, padding: "3px 10px",
                            borderBottom: "1px solid rgba(255,255,255,0.03)", fontSize: 10,
                          }}>
                            <span style={{ color: "#94a3b8", fontWeight: 600, minWidth: 20, fontSize: 10 }}>#{log.pickNumber}</span>
                            <span className="tag" style={{ background: posColor.bg, color: posColor.text, fontSize: 8, padding: "0px 4px", minWidth: 22 }}>{log.player.pos}</span>
                            <span style={{ flex: 1, fontWeight: 500, color: "#e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{log.player.name}</span>
                            <span style={{ fontWeight: 700, color: "#FF9500", fontSize: 10, minWidth: 26, textAlign: "right" }}>${log.price}</span>
                            <span style={{ color: "#334155", fontSize: 10, minWidth: 50, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{log.winnerName}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ---- COMPLETE PHASE ---- */}
        {mockPhase === "complete" && (
          <ResultsScreen
            mockDraftLog={mockDraftLog}
            mockTeams={mockTeams}
            mockUserSlot={mockUserSlot}
            mockPickNumber={mockPickNumber}
            resetMockDraft={resetMockDraft}
            getPlayerProj={getPlayerProj}
            settings={settings}
            onExportText={setMockExportText}
            teamCount={settings.teams}
            dynamicPrices={dynamicPrices}
            userSeatAiDriven={userSeatAiDriven}
            // The id the draft record was persisted under. Read from the
            // recorder rather than regenerated — emailing a different id would
            // deliver someone else's draft or 404. Null on a resumed draft,
            // which hides the email ask entirely.
            draftId={draftLogRef.current?.state?.draftId || null}
          />
        )}
      </div>
      )}

      {/* View All Rosters Modal */}
      <ViewAllRostersModal
        isOpen={showAllRosters}
        onClose={closeAllRosters}
        teams={mockTeams}
        userSlot={mockUserSlot}
        rosterSlots={rosterSlots}
        draftLog={mockDraftLog}
      />

      {/* Slot Assignment Modal */}
      {assigningPlayer && (
        <div className="modal-overlay" onClick={() => setAssigningPlayer(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: fonts.display, fontWeight: 700, marginBottom: 4 }}>{assigningPlayer.name}</h3>
            <p style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>Choose a roster slot</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {getAvailableSlots(assigningPlayer).map(slot => (
                <button key={slot.id} className="btn" onClick={() => addPlayer(assigningPlayer, slot.id)} style={{
                  background: "#0d1320", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "10px 14px",
                  color: "#e2e8f0", fontSize: 13, fontWeight: 500, textAlign: "left", fontFamily: "inherit",
                  display: "flex", alignItems: "center", gap: 10,
                }}>
                  <span className="tag" style={{ background: POS_COLORS[slot.pos].bg, color: "#fff", fontSize: 10, minWidth: 36 }}>{slot.label.replace(/\d+/, '')}</span>
                  {slot.label}
                </button>
              ))}
            </div>
            <button className="btn" onClick={() => setAssigningPlayer(null)} style={{ marginTop: 12, background: "none", color: "#94a3b8", fontSize: 12, fontFamily: "inherit", width: "100%", padding: 8 }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Draft Pick Modal */}
      {draftingPlayer && (
        <div className="modal-overlay" onClick={() => setDraftingPlayer(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <h3 style={{ fontFamily: fonts.display, fontWeight: 700, marginBottom: 4 }}>Draft {draftingPlayer.name}</h3>
            <p style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>
              {draftingPlayer.pos} • {draftingPlayer.team} • Value: ${draftingPlayer.value || 1} • VORP: {getPlayerVorp(draftingPlayer)}
            </p>
            {/* Advisory only — Confirm below stays enabled. The board is a
                tracker: the pick may already have happened in the real room. */}
            <AvoidWarning name={draftingPlayer.name} avoids={myAvoids} style={{ marginBottom: 16 }} />
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: "#94a3b8", letterSpacing: "1px", fontWeight: 600, display: "block", marginBottom: 6 }}>PRICE</label>
              <input autoFocus value={draftPrice} onChange={e => setDraftPrice(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && draftTeamId !== null) confirmDraft(); }}
                type="number" min="0"
                style={{ background: "#0d1320", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "10px 14px", color: "#e2e8f0", fontSize: 18, fontWeight: 700, width: "100%", fontFamily: "inherit", textAlign: "center" }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: "#94a3b8", letterSpacing: "1px", fontWeight: 600, display: "block", marginBottom: 6 }}>NOMINATED BY</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                {draftTeams.map(team => {
                  const selected = draftNominatorId === team.id;
                  return (
                    <button key={team.id} className="btn" onClick={() => setDraftNominatorId(team.id)} style={{
                      background: selected ? "#FF6B00" : "#0d1320",
                      color: selected ? "#000" : "#e2e8f0",
                      border: selected ? "2px solid #FF9500" : "1px solid rgba(255,255,255,0.06)",
                      borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                    }}>
                      {team.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: "#94a3b8", letterSpacing: "1px", fontWeight: 600, display: "block", marginBottom: 6 }}>DRAFTED BY</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                {draftTeams.map(team => {
                  const budget = teamBudgets[team.id] || { remaining: BUDGET };
                  const selected = draftTeamId === team.id;
                  return (
                    <button key={team.id} className="btn" onClick={() => setDraftTeamId(team.id)} style={{
                      background: selected ? "#FF6B00" : "#0d1320",
                      color: selected ? "#fff" : "#e2e8f0",
                      border: selected ? "2px solid #FF9500" : "1px solid rgba(255,255,255,0.06)",
                      borderRadius: 8, padding: "8px 10px", fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}>
                      <span>{team.name}</span>
                      <span style={{ color: selected ? "rgba(255,255,255,0.7)" : "#64748b", fontSize: 11 }}>${budget.remaining}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn" onClick={confirmDraft} disabled={draftTeamId === null || !draftPrice} style={{
                flex: 1, background: draftTeamId !== null && draftPrice ? "linear-gradient(135deg, #FF6B00, #E55A00)" : "#1e293b",
                color: draftTeamId !== null && draftPrice ? "#fff" : "#475569",
                padding: "10px", borderRadius: 8, fontSize: 14, fontWeight: 700, fontFamily: "inherit",
              }}>Confirm Pick</button>
              <button className="btn" onClick={() => setDraftingPlayer(null)} style={{
                background: "none", color: "#94a3b8", fontSize: 13, padding: "10px 16px", fontFamily: "inherit",
              }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Draft Recap Modal */}
      {showDraftRecap && (
        <div className="modal-overlay" onClick={() => setShowDraftRecap(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700, maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontFamily: fonts.display, fontWeight: 700 }}>Draft Recap</h3>
              <button className="btn" onClick={() => setShowDraftRecap(false)} style={{ background: "none", color: "#64748b", fontSize: 20, padding: 4, fontFamily: "inherit" }}>×</button>
            </div>
            {draftPicks.length === 0 ? (
              <p style={{ color: "#94a3b8", fontSize: 13 }}>No picks recorded yet.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {draftTeams.map(team => {
                  const picks = teamRosters[team.id] || [];
                  const budget = teamBudgets[team.id] || { spent: 0, remaining: BUDGET, filled: 0 };
                  const totalProj = picks.reduce((s, p) => s + getPlayerProj(p.player), 0);
                  return (
                    <div key={team.id} style={{ background: "#0d1320", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: "14px 16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: team.name === "Ben" ? "#FF9500" : "#e2e8f0" }}>{team.name}</span>
                        <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#94a3b8" }}>
                          <span>${budget.spent} spent</span>
                          <span>${budget.remaining} left</span>
                          <span>{totalProj} proj</span>
                        </div>
                      </div>
                      {picks.length === 0 ? (
                        <div style={{ fontSize: 12, color: "#334155", fontStyle: "italic" }}>No picks</div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          {picks.map((pick, j) => {
                            const posColor = POS_COLORS[pick.player.pos];
                            const nomName = draftTeams.find(t => t.id === pick.nominatorId)?.name || "?";
                            return (
                              <div key={j} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0" }}>
                                <span className="tag" style={{ background: posColor.bg, color: posColor.text, fontSize: 9, padding: "1px 6px", minWidth: 30 }}>{pick.player.pos}</span>
                                <span style={{ flex: 1, fontSize: 12, fontWeight: 500 }}>{pick.player.name}</span>
                                <span style={{ fontSize: 10, color: "#FF6B00" }} title="Nominated by">nom: {nomName}</span>
                                <span style={{ fontSize: 12, fontWeight: 700, color: "#FF9500" }}>${pick.price}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Export text fallback modal (when clipboard is unavailable) */}
      {mockExportText && (
        <div className="modal-overlay" onClick={() => setMockExportText(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500, padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 16, color: "#FF6B00", margin: 0 }}>Export Results</h3>
              <button onClick={() => setMockExportText(null)} style={{ background: "none", border: "none", color: "#64748b", fontSize: 20, cursor: "pointer" }}>{"\u2715"}</button>
            </div>
            <p style={{ color: "#94a3b8", fontSize: 12, marginBottom: 8 }}>Select all and copy the text below:</p>
            <textarea readOnly value={mockExportText} onFocus={e => e.target.select()} style={{
              width: "100%", height: 300, background: "#0a0f1a", color: "#e2e8f0", border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 8, padding: 12, fontSize: 11, fontFamily: "monospace", resize: "vertical",
            }} />
          </div>
        </div>
      )}
      {/* ── Draft paused — Resume ────────────────────────────────────────────
          The auction loop is gated by loopIdle (useMockDraft.js:1480), and
          Effect 2 returns at :1503 when it is set, writing no state. Nothing
          re-triggers the effect, so a flag that never clears is a permanently
          dead draft — nomination AND bidding both, since every loop effect
          shares the gate.

          Four of the five flags normally correspond to a visible modal, but
          each has failure modes where the flag outlives its UI (mockPaused had
          the All Rosters stale-restore, fixed above; tutorialActive stays true
          whenever GuidedTutorial bails out early — see :80/:83/:173 there —
          which renders no Skip button to clear it). Rather than chase every
          leak, this gives the state an escape hatch that does not depend on
          knowing which flag stuck.

          Deliberately hidden while a modal is open: that case is self-evident
          and closable. The failure state is an idle loop with nothing on
          screen, which is exactly when this shows. Never auto-resumes. */}
      {appMode === "mock"
        && (mockPhase === "nominating" || mockPhase === "bidding" || mockPhase === "sold")
        && (mockPaused || tutorialActive || mockLeaveTarget !== null || showSettings || mockShowRecap)
        && !(showAllRosters || showSettings || mockShowRecap || mockLeaveTarget !== null) && (
        <div style={{
          position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: 24,
          zIndex: 4000, display: "flex", alignItems: "center", gap: 14,
          padding: "12px 18px", borderRadius: 10,
          background: "rgba(10,15,26,0.97)", border: "1px solid rgba(255,107,0,0.45)",
          boxShadow: "0 8px 28px rgba(0,0,0,0.55)",
        }}>
          <span style={{ color: "#e2e8f0", fontSize: 14, fontWeight: 600 }}>Draft paused</span>
          <button
            onClick={() => {
              // Clear ALL five, not just the one we think is set: the whole
              // point is to work without knowing which leaked.
              setMockPaused(false);
              setMockLeaveTarget(null);
              setShowSettings(false);
              setMockShowRecap(false);
              setTutorialHint(null);
            }}
            style={{
              padding: "9px 22px", borderRadius: 8, fontSize: 14, fontWeight: 700,
              cursor: "pointer", fontFamily: "inherit",
              background: "rgba(255,107,0,0.18)", color: "#FF9500",
              border: "1px solid rgba(255,107,0,0.45)",
            }}
          >Resume</button>
        </div>
      )}

      {/* Leave draft confirmation modal */}
      {mockLeaveTarget && (
        <div className="modal-overlay" onClick={() => setMockLeaveTarget(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 360, textAlign: "center", padding: 32 }}>
            <h3 style={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 18, color: "#FF6B00", marginBottom: 8 }}>
              Draft in Progress
            </h3>
            <p style={{ color: "#94a3b8", fontSize: 14, marginBottom: 24 }}>Leave and lose all draft progress?</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button onClick={() => setMockLeaveTarget(null)} style={{
                padding: "10px 28px", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                background: "rgba(255,107,0,0.15)", color: "#FF9500", border: "1px solid rgba(255,107,0,0.3)",
              }}>Stay</button>
              <button onClick={() => { resetMockDraft(); if (mockLeaveTarget === "landing") { setShowLanding(true); } else { setAppMode(mockLeaveTarget); } setMockLeaveTarget(null); }} style={{
                padding: "10px 28px", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                background: "rgba(239,68,68,0.15)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.3)",
              }}>Leave</button>
            </div>
          </div>
        </div>
      )}

      {mockShowRecap && (
        <div className="modal-overlay" onClick={() => setMockShowRecap(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700, maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontFamily: fonts.display, fontWeight: 700,
                background: "linear-gradient(135deg, #FF6B00, #E55A00)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Mock Draft Recap</h3>
              <button className="btn" onClick={() => setMockShowRecap(false)} style={{ background: "none", color: "#64748b", fontSize: 20, padding: 4, fontFamily: "inherit" }}>×</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {Array.from({ length: settings.teams }, (_, i) => {
                const team = mockTeams[i];
                if (!team) return null;
                const totalProj = team.roster.reduce((s, p) => s + getPlayerProj(p.player), 0);
                const slotDefs = rosterSlots;
                const filled = {};
                const posQueues = {};
                team.roster.forEach(pick => {
                  const pos = pick.player.pos;
                  if (!posQueues[pos]) posQueues[pos] = [];
                  posQueues[pos].push(pick);
                });
                Object.values(posQueues).forEach(q => q.sort((a, b) => b.price - a.price));
                slotDefs.forEach(slot => {
                  if (slot.pos !== "FLEX" && slot.pos !== "BN" && slot.pos !== "SFLEX") {
                    const q = posQueues[slot.pos];
                    if (q && q.length > 0) filled[slot.id] = q.shift();
                  }
                });
                const recapFlexOrder = (() => {
                  const fp = team.profile?.flexPos;
                  if (fp === "WR") return ["WR", "RB", "TE"];
                  if (fp === "RB") return ["RB", "WR", "TE"];
                  return ["RB", "WR", "TE"];
                })();
                // QB first — a spare QB can ONLY start in SFLEX; skill players are the fallback.
                slotDefs.filter(s => s.pos === "SFLEX").forEach(slot => {
                  for (const pos of ["QB", ...recapFlexOrder]) {
                    if (posQueues[pos] && posQueues[pos].length > 0) { filled[slot.id] = posQueues[pos].shift(); break; }
                  }
                });
                slotDefs.filter(s => s.pos === "FLEX").forEach(slot => {
                  for (const pos of recapFlexOrder) {
                    if (posQueues[pos] && posQueues[pos].length > 0) { filled[slot.id] = posQueues[pos].shift(); break; }
                  }
                });
                slotDefs.filter(s => s.pos === "BN").forEach(slot => {
                  for (const pos of ["RB", "WR", "QB", "TE", "DEF", "K"]) {
                    if (posQueues[pos] && posQueues[pos].length > 0) { filled[slot.id] = posQueues[pos].shift(); break; }
                  }
                });
                const posSpend = {};
                team.roster.forEach(p => { posSpend[p.player.pos] = (posSpend[p.player.pos] || 0) + p.price; });
                return (
                  <div key={i} style={{
                    background: team.isUser ? "rgba(255,107,0,0.06)" : "#0d1320",
                    border: `1px solid ${team.isUser ? "rgba(255,107,0,0.2)" : "rgba(255,255,255,0.06)"}`,
                    borderRadius: 10, overflow: "hidden",
                  }}>
                    <div style={{ padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: team.isUser ? "#FF6B00" : "#e2e8f0" }}>
                          {team.name}{team.isUser ? " (You)" : ""}
                        </span>
                        <div style={{ display: "flex", gap: 10, fontSize: 11, color: "#94a3b8" }}>
                          <span>${settings.budget - team.budget} spent</span>
                          <span style={{ color: "#FF9500", fontWeight: 600 }}>{totalProj} pts</span>
                        </div>
                      </div>
                      {!team.isUser && team.profile && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 10, fontWeight: 600, color: "#FF6B00", background: "rgba(255,107,0,0.1)", padding: "1px 6px", borderRadius: 4 }}>{team.profile.archetype}</span>
                          {(team.profile.modifiers || []).map(mod => (
                            <span key={mod} style={{ fontSize: 9, fontWeight: 500, color: mod === "Enforcer" ? "#f87171" : mod === "Homer" ? "#60a5fa" : "#a78bfa", background: mod === "Enforcer" ? "rgba(248,113,113,0.1)" : mod === "Homer" ? "rgba(96,165,250,0.1)" : "rgba(167,139,250,0.1)", padding: "1px 5px", borderRadius: 3 }}>
                              {mod}{mod === "Homer" && team.profile.favoriteTeam ? ` (${team.profile.favoriteTeam})` : ""}
                            </span>
                          ))}
                          <span style={{ fontSize: 10, color: "#94a3b8", marginLeft: 2 }}>
                            Target: QB${team.profile.qb$} RB${team.profile.rb$} WR${team.profile.wr$} TE${team.profile.te$}
                          </span>
                        </div>
                      )}
                    </div>
                    {team.roster.length === 0 ? (
                      <div style={{ padding: 12, fontSize: 11, color: "#334155", fontStyle: "italic" }}>No picks yet</div>
                    ) : (
                      <div>
                        {slotDefs.map(slot => {
                          const pick = filled[slot.id];
                          const posColor = POS_COLORS[slot.pos];
                          return (
                            <div key={slot.id} style={{
                              display: "flex", alignItems: "center", padding: "4px 14px", gap: 8,
                              borderBottom: "1px solid rgba(255,255,255,0.02)", minHeight: 26,
                            }}>
                              <span className="tag" style={{ background: posColor.bg, color: posColor.text, fontSize: 9, padding: "1px 5px", minWidth: 34, textAlign: "center" }}>
                                {slot.label.replace(/\d+/, '')}
                              </span>
                              {pick ? (
                                <>
                                  <span style={{ flex: 1, fontSize: 11, fontWeight: 500 }}>{pick.player.name}</span>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: "#FF9500", minWidth: 30, textAlign: "right" }}>${pick.price}</span>
                                </>
                              ) : (
                                <span style={{ flex: 1, fontSize: 11, color: "rgba(255,255,255,0.1)", fontStyle: "italic" }}>—</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div style={{ padding: "6px 14px", borderTop: "1px solid rgba(255,255,255,0.04)", display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {["QB", "RB", "WR", "TE"].map(pos => {
                        const actual = posSpend[pos] || 0;
                        const target = !team.isUser && team.profile ? team.profile[pos.toLowerCase() + "$"] : null;
                        const diff = target != null ? actual - target : null;
                        return (
                          <span key={pos} style={{ fontSize: 10, color: "#94a3b8" }}>
                            <span style={{ color: POS_COLORS[pos]?.bg, fontWeight: 600 }}>{pos}:</span> ${actual}
                            {diff != null && <span style={{ color: diff > 5 ? "#f87171" : diff < -5 ? "#60a5fa" : "#475569", fontSize: 9 }}> ({diff >= 0 ? "+" : ""}{diff})</span>}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Save Build Dialog */}
      {showSaveBuildDialog && (
        <div onClick={() => setShowSaveBuildDialog(false)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1100,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "#131b2e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12,
            padding: 20, width: 300, boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
            animation: "slideIn 0.2s ease-out",
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0", marginBottom: 12, fontFamily: fonts.display }}>Save Build</div>
            <input type="text" value={buildName} onChange={e => setBuildName(e.target.value)}
              placeholder="Build name..." autoFocus
              onKeyDown={e => e.key === "Enter" && saveBuild()}
              style={{
                width: "100%", background: "#0f172a", border: "1px solid #334155", borderRadius: 6,
                padding: "8px 12px", color: "#e2e8f0", fontSize: 13, fontFamily: "inherit",
                marginBottom: 12,
              }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn" onClick={saveBuild}
                style={{
                  flex: 1, background: buildName.trim() ? "linear-gradient(135deg, #FF6B00, #E55A00)" : "#1e293b",
                  color: buildName.trim() ? "#fff" : "#475569",
                  padding: "8px 14px", borderRadius: 6, fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                  border: "none", cursor: buildName.trim() ? "pointer" : "default",
                }}>Save</button>
              <button className="btn" onClick={() => setShowSaveBuildDialog(false)}
                style={{
                  background: "#334155", color: "#94a3b8", padding: "8px 14px", borderRadius: 6,
                  fontSize: 12, fontWeight: 600, fontFamily: "inherit", border: "none",
                }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Saved Builds Comparison Modal */}
      {showSaved && (() => {
        // Filter to the active league by default; "All leagues" shows every build
        // (legacy builds with no league field only ever appear under All leagues).
        const visibleBuilds = showAllBuildLeagues
          ? savedBuilds
          : savedBuilds.filter(b => b.league === activeLeagueName);
        const canCompare = visibleBuilds.length >= 2;
        // Analyze each build
        const analyzed = visibleBuilds.map(build => {
          const players = Object.values(build.roster);
          const scoring = build.settings?.scoring || settings.scoring;
          const positions = build.settings?.positions || settings.positions;
          const budget = build.settings?.budget || settings.budget;
          const slots = buildRosterSlots(positions);
          const starterSlots = slots.filter(s => s.pos !== "BN");
          const starters = [];
          const bench = [];
          // Map roster to slots to identify starters vs bench
          for (const slot of slots) {
            const p = build.roster[slot.id];
            if (p) { if (slot.pos !== "BN") starters.push(p); else bench.push(p); }
          }
          // Calculate projections
          let totalProj = 0;
          let starterProj = 0;
          // Through the projection layer like every other consumer. `scoring` here
          // is deliberately the BUILD's stored lens, not the current league's — a
          // saved build is re-scored under the rules it was built for.
          players.forEach(p => { totalProj += projectPoints(p, scoring); });
          starters.forEach(p => { starterProj += projectPoints(p, scoring); });
          // 🔴 Per player, not starterProj / 17. projectGames returns the games
          // THIS projection was normalised to — ~15 for skill players, 17 for
          // DEF/K — so a season total has no single divisor. Same reason
          // week1Projection sums a rate at a time.
          const starterPPG = starters.length > 0
            ? starters.reduce((s, p) => s + projectPoints(p, scoring) / projectGames(p), 0).toFixed(1)
            : "0.0";
          // Spend by position
          const posSpend = {};
          players.forEach(p => { posSpend[p.pos] = (posSpend[p.pos] || 0) + (p.savedPrice ?? p.price ?? 0); });
          const totalSpent = players.reduce((s, p) => s + (p.savedPrice ?? p.price ?? 0), 0);
          // Bye week analysis (weeks 5-14)
          const byeWeeks = {};
          for (let w = 5; w <= 14; w++) byeWeeks[w] = 0;
          starters.forEach(p => { if (p.bye >= 5 && p.bye <= 14) byeWeeks[p.bye]++; });
          const worstBye = Object.entries(byeWeeks).sort((a, b) => b[1] - a[1])[0];
          return { build, players, starters, bench, totalProj, starterProj, starterPPG: parseFloat(starterPPG), starterPPGStr: starterPPG, posSpend, totalSpent, budget, byeWeeks, worstBye, starterSlots };
        });
        // Find best values for comparison highlighting
        const best = canCompare ? {
          totalProj: Math.max(...analyzed.map(a => a.totalProj)),
          starterPPG: Math.max(...analyzed.map(a => a.starterPPG)),
          totalSpent: Math.min(...analyzed.map(a => a.totalSpent)),
          worstByeCount: Math.min(...analyzed.map(a => a.worstBye ? a.worstBye[1] : 0)),
        } : null;
        const worst = canCompare ? {
          totalProj: Math.min(...analyzed.map(a => a.totalProj)),
          starterPPG: Math.min(...analyzed.map(a => a.starterPPG)),
          totalSpent: Math.max(...analyzed.map(a => a.totalSpent)),
          worstByeCount: Math.max(...analyzed.map(a => a.worstBye ? a.worstBye[1] : 0)),
        } : null;
        const cmpColor = (val, bestVal, worstVal, higherIsBetter = true) => {
          if (!canCompare || bestVal === worstVal) return "#e2e8f0";
          if (higherIsBetter) return val === bestVal ? "#4ade80" : val === worstVal ? "#f87171" : "#e2e8f0";
          return val === bestVal ? "#4ade80" : val === worstVal ? "#f87171" : "#e2e8f0";
        };
        return (
          <div onClick={() => setShowSaved(false)} style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 1000,
            display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "3vh",
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: "#131b2e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14,
              padding: "20px 18px", maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto",
              boxShadow: "0 16px 48px rgba(0,0,0,0.8)", animation: "slideIn 0.2s ease-out",
              width: canCompare ? Math.min(analyzed.length * 280 + 40, 900) : 420,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <span style={{ fontSize: 16, fontWeight: 800, color: "#e2e8f0", fontFamily: fonts.display }}>
                  {canCompare ? "Compare Builds" : "Saved Builds"}
                </span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button className="btn" onClick={() => setShowAllBuildLeagues(v => !v)} style={{
                    background: showAllBuildLeagues ? "rgba(255,107,0,0.15)" : "none",
                    color: showAllBuildLeagues ? "#FF9500" : "#64748b",
                    border: `1px solid ${showAllBuildLeagues ? "rgba(255,107,0,0.4)" : "#1e293b"}`,
                    borderRadius: 6, padding: "3px 8px", fontSize: 10, fontWeight: 700, fontFamily: "inherit",
                  }}>{showAllBuildLeagues ? "● All leagues" : "○ All leagues"}</button>
                  <button className="btn" onClick={() => setShowSaved(false)}
                    style={{ background: "none", color: "#64748b", fontSize: 18, padding: 4, fontFamily: "inherit", border: "none" }}>×</button>
                </div>
              </div>
              {visibleBuilds.length === 0 ? (
                <div style={{ fontSize: 12, color: "#94a3b8", textAlign: "center", padding: 20 }}>
                  {savedBuilds.length === 0
                    ? "No saved builds yet"
                    : showAllBuildLeagues
                      ? "No saved builds yet"
                      : `No builds for ${activeLeagueName || "this league"} — toggle "All leagues" to see the other ${savedBuilds.length}.`}
                </div>
              ) : (
                <div style={{ display: "flex", gap: 10, overflowX: analyzed.length > 2 ? "auto" : "visible", paddingBottom: 4 }}>
                  {analyzed.map(a => (
                    <div key={a.build.id || a.build.timestamp} style={{
                      background: "#0f172a", border: "1px solid #1e293b", borderRadius: 10, padding: "12px",
                      minWidth: canCompare ? 250 : undefined, flex: canCompare ? "0 0 250px" : 1,
                    }}>
                      {/* Header */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>{a.build.name}</span>
                        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                          <button className="btn" onClick={() => loadBuild(a.build)} style={{
                            background: "#FF6B00", color: "#fff", padding: "2px 8px", borderRadius: 4,
                            fontSize: 9, fontWeight: 700, fontFamily: "inherit",
                          }}>Load</button>
                          <button onClick={() => deleteBuild(a.build.id || a.build.timestamp)}
                            style={{ background: "none", border: "none", color: "#475569", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>×</button>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 10, color: "#94a3b8" }}>{new Date(a.build.timestamp).toLocaleDateString()}</span>
                        <span style={{
                          fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                          background: a.build.league ? "rgba(255,107,0,0.12)" : "#1e293b",
                          color: a.build.league ? "#FF9500" : "#64748b",
                          border: `1px solid ${a.build.league ? "rgba(255,107,0,0.3)" : "#334155"}`,
                        }}>{a.build.league || "No league"}</span>
                      </div>

                      {/* Key Metrics */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 10 }}>
                        <div style={{ background: "#0a0f1a", borderRadius: 6, padding: "6px 8px", border: "1px solid #1e293b" }}>
                          <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.5px" }}>TOTAL PROJ</div>
                          <div style={{ fontSize: 16, fontWeight: 800, color: cmpColor(a.totalProj, best?.totalProj, worst?.totalProj) }}>{a.totalProj}</div>
                        </div>
                        <div style={{ background: "#0a0f1a", borderRadius: 6, padding: "6px 8px", border: "1px solid #1e293b" }}>
                          <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.5px" }}>STARTER PPG</div>
                          <div style={{ fontSize: 16, fontWeight: 800, color: cmpColor(a.starterPPG, best?.starterPPG, worst?.starterPPG) }}>{a.starterPPGStr}</div>
                        </div>
                        <div style={{ background: "#0a0f1a", borderRadius: 6, padding: "6px 8px", border: "1px solid #1e293b" }}>
                          <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.5px" }}>SPENT</div>
                          <div style={{ fontSize: 16, fontWeight: 800, color: cmpColor(a.totalSpent, best?.totalSpent, worst?.totalSpent, false) }}>${a.totalSpent}</div>
                        </div>
                        <div style={{ background: "#0a0f1a", borderRadius: 6, padding: "6px 8px", border: "1px solid #1e293b" }}>
                          <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.5px" }}>REMAINING</div>
                          <div style={{ fontSize: 16, fontWeight: 800, color: "#e2e8f0" }}>${a.budget - a.totalSpent}</div>
                        </div>
                      </div>

                      {/* Position Spend */}
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.5px", marginBottom: 4 }}>POSITION SPEND</div>
                        <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                          {["QB","RB","WR","TE","DEF","K"].filter(pos => a.posSpend[pos]).map(pos => (
                            <span key={pos} style={{
                              fontSize: 9, fontWeight: 700, padding: "2px 5px", borderRadius: 4,
                              background: (POS_COLORS[pos] || {}).bg || "#1e293b",
                              color: (POS_COLORS[pos] || {}).text || "#94a3b8",
                            }}>{pos} ${a.posSpend[pos]}</span>
                          ))}
                        </div>
                      </div>

                      {/* Bye Week Analysis */}
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.5px", marginBottom: 4 }}>BYE WEEKS</div>
                        <div style={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                          {Object.entries(a.byeWeeks).map(([week, count]) => (
                            <span key={week} style={{
                              fontSize: 8, fontWeight: 700, padding: "2px 4px", borderRadius: 3, minWidth: 18, textAlign: "center",
                              background: count >= 3 ? "rgba(239,68,68,0.2)" : count >= 2 ? "rgba(251,191,36,0.1)" : "#0a0f1a",
                              color: count >= 3 ? "#f87171" : count >= 2 ? "#fbbf24" : "#475569",
                              border: count >= 3 ? "1px solid rgba(239,68,68,0.3)" : "1px solid #1e293b",
                            }}>W{week}: {count}</span>
                          ))}
                        </div>
                        {a.worstBye && a.worstBye[1] > 0 && (
                          <div style={{
                            fontSize: 10, marginTop: 3, fontWeight: 600,
                            color: cmpColor(a.worstBye[1], best?.worstByeCount, worst?.worstByeCount, false),
                          }}>Worst: Week {a.worstBye[0]} ({a.worstBye[1]} starters)</div>
                        )}
                      </div>

                      {/* Starter Roster */}
                      <div>
                        <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.5px", marginBottom: 4 }}>STARTERS</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          {a.starters.map((p, i) => (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 0" }}>
                              <span style={{
                                fontSize: 8, fontWeight: 700, width: 22, textAlign: "center", borderRadius: 3, padding: "1px 0",
                                background: (POS_COLORS[p.pos] || {}).bg || "#1e293b",
                                color: (POS_COLORS[p.pos] || {}).text || "#94a3b8",
                              }}>{p.pos}</span>
                              <span style={{ fontSize: 10, color: "#e2e8f0", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                              <span style={{ fontSize: 10, color: "#FF9500", fontWeight: 600 }}>${p.savedPrice ?? p.price ?? 0}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Settings Modal. dynamicPrices is built for the APPLIED settings, so the
          analyzer panel only spends it when the unapplied draft still matches —
          see priceMapMatchesDraft in SettingsModal. */}
      {showSettings && (
        <SettingsModal settings={settings} onApply={applySettings} onClose={closeSettings}
          savedLeagues={savedLeagues} setSavedLeagues={setSavedLeagues} activeLeagueName={activeLeagueName} setActiveLeagueName={setActiveLeagueName}
          mockDraftLive={mockDraftLive} dynamicPrices={dynamicPrices}
          startInCreate={settingsCreateIntent} startOnFormatTab={settingsFormatIntent}
          onLiveChange={applyLiveSettings} onFinishCreate={finishLeagueCreate} />
      )}

      {/* League-import confirmation. Deliberately OUTSIDE the settings modal —
          it is the proof the import landed, so it has to be visible on the
          screen the import returns you to. Auto-dismisses in useLeagueSettings. */}
      {importNotice && (
        <div
          role="status"
          onClick={() => setImportNotice(null)}
          style={{
            position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
            maxWidth: "min(420px, calc(100vw - 32px))",
            background: theme.colors.bg.surface, border: `1px solid #059669`,
            borderRadius: 8, padding: "10px 16px", zIndex: 3000, cursor: "pointer",
            boxShadow: "0 6px 20px rgba(0,0,0,0.4)", lineHeight: 1.45,
          }}
        >
          <div style={{ color: "#059669", fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ flex: 1, minWidth: 0 }}>✓ {importNotice.name} loaded</span>
            <span style={{ color: theme.colors.text.faint, fontSize: 14, fontWeight: 400, flexShrink: 0 }}>×</span>
          </div>
          <div style={{ fontSize: 10, color: theme.colors.text.secondary }}>{importNotice.summary}</div>
        </div>
      )}

      {/* Heartbeat vignette */}
      {(heartbeatPulse !== undefined) && mockGoingPhase && (mockGoingPhase === "once" || mockGoingPhase === "twice") && mockCurrentBidder === mockUserSlot && (
        <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 50, transition: "box-shadow 0.15s ease-out", boxShadow: heartbeatPulse ? "inset 0 0 120px 40px rgba(255,60,0,0.15)" : "inset 0 0 60px 10px rgba(255,60,0,0.04)" }} />
      )}

      {/* Your Turn nomination overlay */}
      {mockNominateOverlay && <div onClick={() => { setMockNominateOverlay(false); setMockNomGlow(true); setTimeout(() => setMockNomGlow(false), 3000); }} style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', background:'#FF6B00', cursor:'pointer', animation:'overlayFadeIn 0.2s ease-out' }}><div style={{ textAlign:'center', animation:'overlayScaleIn 0.3s cubic-bezier(0.34,1.56,0.64,1)' }}><div style={{ fontFamily:"'Exo 2',sans-serif", fontSize:52, fontWeight:900, color:'#000', textTransform:'uppercase', letterSpacing:2, lineHeight:1.1 }}>YOUR TURN</div><div style={{ fontFamily:"'Exo 2',sans-serif", fontSize:16, fontWeight:700, color:'rgba(0,0,0,0.6)', letterSpacing:6, textTransform:'uppercase', marginTop:8 }}>NOMINATE A PLAYER</div><div className="tap-continue-pulse" style={{ fontFamily:"'Figtree',sans-serif", fontSize:13, fontWeight:500, color:'rgba(255,255,255,0.6)', marginTop:24, letterSpacing:1 }}>{isTouch ? "Tap" : "Click"} to continue</div></div></div>}
    </div>
  );
}

